import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BoundingBox,
  LinkedPfdRef,
  LinkedPnidRef,
  PidDocument,
  PnidEquipment,
  PnidInstrument,
  PnidLinkedPfdStreamRef,
  PnidNode,
  PnidWizardState,
  PnidAIData,
  NodePointCandidate,
  TemplateAnnotation,
  DRAIN_TYPES,
  getEquipmentInstrumentName,
  getInstrumentCategoryName,
  isDrainDestination,
  INSTRUMENT_LINE_SUBTYPE_LABELS,
  InstrumentLineSubtype,
  LineType,
  InstallationType,
  INSTALLATION_TYPE_LABELS,
  TracingType,
  TRACING_TYPE_LABELS,
  FlowProfile,
  FLOW_PROFILE_LABELS,
  FlowDirection,
  FLOW_DIRECTION_LABELS,
  PrimaryDirection,
  PRIMARY_DIRECTION_LABELS,
  RegisterEquipmentContext,
} from '../types';
import DomainSelect from './DomainSelect';
import SourceDestSelector, { InstrumentRef } from './SourceDestSelector';
import { isCompoundTag } from '../utils/compoundTag';
import CompoundSplitEditor from './CompoundSplitEditor';
import { getInstrumentPrimaryLabel } from '../utils/instrumentIdentity';
import { getInstrumentEffectiveContext } from '../utils/unifiedInstruments';
import SchemaFieldRenderer, { CustomRendererMap } from './SchemaFieldRenderer';
import TagAutocompleteInput from './TagAutocompleteInput';
import RedundancySetSection from './RedundancySetSection';
import { useFormSchema } from '../hooks/useFormSchemas';
import { useEquipmentTypes } from '../hooks/useEquipmentTypes';
import {
  describePrimaryDrawing,
  getEquipmentRegisterContext,
  ownedByAnotherDrawing,
  primaryDrawing,
  resolvePrimaryMark,
  useInvalidateRegisterContext,
  useRegisterContext,
  useTagRegisterContext,
} from '../hooks/useRegisterContext';
import { buildEquipmentEditHref } from '../utils/pnidDeepLink';

export type PnidDialogKind = 'equipment' | 'node';

/** Ties the Domain row's label to its select, which is not nested inside it. */
const OWNER_DOMAIN_SELECT_ID = 'pnid-mark-owner-domain';

/** The Primary P&ID question, set apart from the fields the answer governs. */
const PRIMARY_SOURCE_PANEL_STYLE: React.CSSProperties = {
  padding: 10,
  background: 'var(--raven-surface-tier2)',
  border: '1px solid var(--raven-divider)',
  borderRadius: 6,
};

/**
 * Collapse space/hyphen/underscore variants of a tag, as the register's
 * `normalize_asset_tag` does — "LG 402", "LG-402" and "lg_402" all name the one
 * asset. Re-spelling a tag is therefore not renaming it.
 */
function normalizeTag(tag: string): string {
  return tag.trim().toUpperCase().replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
}

interface PnidMarkDialogProps {
  kind: PnidDialogKind;
  /** Every region the SME drew for this marking, in draw order. Empty while editing an existing one. */
  bboxes: BoundingBox[];
  pnidNoDefault?: string | null;
  activeEquipment?: PnidEquipment | null;
  activeNode?: PnidNode | null;
  equipmentInstruments?: PnidInstrument[];
  nodeInstruments?: PnidInstrument[];
  directNodeInstruments?: PnidInstrument[];
  nodes?: PnidNode[];  // For parent node selection in bifurcating lines
  equipments?: PnidEquipment[];  // Manually created equipment for dropdowns
  instruments?: InstrumentRef[];  // Unified instruments — for node source/dest selector's Instrument tab
  aiData?: PnidAIData | null;  // AI-extracted data for dropdowns
  currentDocument?: PidDocument | null;
  allDocuments?: PidDocument[];
  linkedDocumentStates?: Record<string, PnidWizardState>;
  onLoadLinkedDocumentState?: (docId: string) => Promise<void>;
  onOpenLinkedDocument?: (target: {
    docId: string;
    targetEquipmentId?: string | null;
    targetNodeId?: string | null;
    newTab?: boolean;
  }) => void;
  initialValues?: Record<string, any>;
  diagramMode?: 'pid' | 'pfd';
  onSubmit: (values: Record<string, any>) => void;
  onCancel: () => void;
  // For marking existing items from AI prefill
  isMarkingExisting?: boolean;
  // For line drawing: continue drawing or clear path
  onContinueDrawing?: () => void;  // Close dialog, keep path on canvas
  onClearPath?: () => void;         // Clear path and close dialog
  onRedrawPath?: () => void;        // Remove current line and start fresh redraw
  // For node instrument sequence management (when kind === 'node')
  onEditInstrument?: (instrumentId: string) => void;
  onUnattachInstrument?: (instrumentId: string) => void;
  // For equipment instrument management (when kind === 'equipment')
  onEditEquipmentInstrument?: (instrumentId: string) => void;
  onUnattachEquipmentInstrument?: (instrumentId: string) => void;
  onReorderInstruments?: (reordered: PnidInstrument[]) => void;
  onAutoSequence?: (nodeId: string) => Promise<void>;
  onAutoAddBranches?: (nodeId: string) => Promise<{ created: number; skipped: number }>;
  branchCandidates?: NodePointCandidate[];
  onConfirmBranchCandidate?: (candidateId: string) => void;
  onIgnoreBranchCandidate?: (candidateId: string) => void;
  // Template attachment props (for node instrument management)
  draftAttachedAnnotationIds?: string[];
  templateAnnotations?: TemplateAnnotation[];
  onSaveAttachments?: () => Promise<void>;
  onDetachAnnotation?: (annotationId: string) => void;
  onHighlightEntity?: (id: string | null) => void;
  /** Callback to enter instrument drawing mode (auto-attaches to the given context) */
  onMarkInstrument?: (contextType: 'equipment' | 'node', contextId: string) => void;
  onMarkReviewed?: (target: {
    kind: 'equipment' | 'equipmentInstrument' | 'node' | 'nodeInstrument';
    id: string;
  }) => Promise<void> | void;
  /**
   * Splits the equipment being edited into the given assets: the first part
   * renames it, every later part becomes a new equipment on this drawing.
   * Supplied only while an existing P&ID equipment is open, which is also what
   * makes the compound-tag warning offer the split at all. Rejects with the
   * message to show the reviewer, including what the run managed to save.
   */
  onSplitCompoundEquipment?: (
    parts: Array<{ equipment_no: string; description: string }>,
  ) => Promise<void>;
  /**
   * The stored equipment type the split's new assets would inherit — the saved
   * value, not the one in this form, because that is what the split sends. The
   * create route admits reviewed types only, so an unreviewed one is caught here
   * rather than half-way through the split.
   */
  compoundSplitInheritedType?: string | null;
  readOnly?: boolean;
}


const inferDefaultContextualType = (_kind: PnidDialogKind, values: Record<string, any>) => {
  const rawText = `${values.instrument_text ?? ''} ${values.subtype ?? ''} ${values.instrument_type ?? ''}`.toUpperCase();
  const hasAny = (terms: string[]) => terms.some((t) => rawText.includes(t));

  if (hasAny(['HPV', 'HPD', 'HIGH POINT VENT', 'HIGHPOINT VENT'])) return 'HPV';
  if (hasAny(['LPD', 'LOW POINT DRAIN', 'LOWPOINT DRAIN'])) return 'LPD';
  if (hasAny(['DV', 'DRAIN VALVE', 'DRAIN'])) return 'DV';
  if (hasAny(['VV', 'VENT VALVE', 'VENT'])) return 'VV';
  if (hasAny(['BV', 'BYPASS'])) return 'BV';
  if (hasAny(['BK', 'BLOCK'])) return 'BK';
  if (hasAny(['BL', 'BLEED'])) return 'BL';
  // NRV / Check valve detection
  if (hasAny(['NRV', 'NON RETURN', 'CHECK VALVE', 'NON-RETURN'])) return 'NRV';

  if (hasAny(['ISOLATION', 'IV'])) return 'IV';

  const valveKeywords = [
    'GATE VALVE',
    'GLOBE VALVE',
    'BALL VALVE',
    'BUTTERFLY VALVE',
    'NEEDLE VALVE',
    'PLUG VALVE',
    'DIAPHRAGM VALVE',
    'PINCH VALVE',
    'ANGLE VALVE',
    'DAMPER',
    '3-WAY',
    '3 WAY',
    'Y-TYPE',
    'JACKETED',
  ];
  if (hasAny(valveKeywords)) return 'IV';

  if (values.instrument_type === 'Valve') return 'IV';

  return null;
};

// === Autocomplete Dropdown Component ===
interface AutocompleteOption {
  value: string;
  label: string;
}

interface AutocompleteDropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: AutocompleteOption[];
  placeholder?: string;
  allowCustom?: boolean; // If false, only predefined options allowed
  inputRef?: React.RefObject<HTMLInputElement>;
  onCommit?: () => void;
  // When true, render the option's label (not its value) as the primary display
  // in both the input and the dropdown rows. Use when `value` is an opaque id.
  displayLabel?: boolean;
}

const AutocompleteDropdown: React.FC<AutocompleteDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select or type...',
  allowCustom = true,
  inputRef: inputRefProp,
  onCommit,
  displayLabel = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const localInputRef = React.useRef<HTMLInputElement>(null);
  const inputRef = inputRefProp ?? localInputRef;
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const lower = search.toLowerCase();
    return options.filter(opt => 
      opt.value.toLowerCase().includes(lower) || 
      opt.label.toLowerCase().includes(lower)
    );
  }, [options, search]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (val: string) => {
    onChange(val);
    setSearch('');
    setIsOpen(false);
  };

  const displayValue = (() => {
    if (!value) return '';
    if (displayLabel) {
      const matched = options.find((o) => o.value === value);
      if (matched) return matched.label;
    }
    return value;
  })();

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        <input
          ref={inputRef}
          value={isOpen ? search : displayValue}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsOpen(false);
              setSearch('');
              return;
            }
            if (e.key !== 'Enter' && e.key !== 'Tab') return;
            if (e.key === 'Tab' && e.shiftKey) return;
            e.preventDefault();

            const query = search.trim();
            if (isOpen && query) {
              if (filteredOptions.length) {
                handleSelect(filteredOptions[0].value);
              } else if (allowCustom) {
                handleSelect(query);
              } else {
                setIsOpen(false);
                setSearch('');
              }
            } else {
              setIsOpen(false);
              setSearch('');
            }

            onCommit?.();
          }}
          placeholder={placeholder}
          style={{ 
            flex: 1,
            padding: 8, 
            border: '1px solid var(--raven-divider)', 
            borderRadius: 6,
            fontSize: 12
          }}
        />
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) inputRef.current?.focus();
          }}
          style={{
            padding: '8px 10px',
            border: '1px solid var(--raven-divider)',
            borderRadius: 6,
            background: 'white',
            cursor: 'pointer',
            fontSize: 10
          }}
        >
          ▼
        </button>
      </div>
      
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 4,
          background: 'white',
          border: '1px solid var(--raven-divider)',
          borderRadius: 6,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          maxHeight: 200,
          overflowY: 'auto',
          zIndex: 1000
        }}>
          <div
            onClick={() => handleSelect('')}
            style={{
              padding: '8px 12px',
              cursor: 'pointer',
              fontSize: 11,
              color: 'var(--raven-text-disabled)',
              background: value === '' ? 'var(--raven-surface-tier3)' : 'transparent',
              borderBottom: '1px solid var(--raven-surface-tier3)'
            }}
          >
            -- None --
          </div>
          {filteredOptions.map((opt, idx) => (
            <div
              key={idx}
              onClick={() => handleSelect(opt.value)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 11,
                background: value === opt.value ? 'var(--raven-surface-tier2)' : 'transparent',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--raven-surface-tier3)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = value === opt.value ? 'var(--raven-surface-tier2)' : 'transparent')}
            >
              {displayLabel ? (
                <div style={{ fontWeight: 500 }}>{opt.label || opt.value}</div>
              ) : (
                <>
                  <div style={{ fontWeight: 500 }}>{opt.value}</div>
                  {opt.label !== opt.value && (
                    <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)' }}>{opt.label}</div>
                  )}
                </>
              )}
            </div>
          ))}
          {allowCustom && search && !filteredOptions.some(o => o.value === search) && (
            <div
              onClick={() => handleSelect(search)}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                fontSize: 11,
                background: 'var(--raven-surface-tier3)',
                borderTop: '1px solid var(--raven-divider)'
              }}
            >
              <div style={{ fontWeight: 500, color: 'var(--raven-primary-main)' }}>Use "{search}"</div>
              <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)' }}>Custom value</div>
            </div>
          )}
          {!allowCustom && search && filteredOptions.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--raven-error-text)' }}>
              No matching option found. Select from the list.
            </div>
          )}
          {filteredOptions.length === 0 && !search && (
            <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--raven-text-disabled)' }}>
              No options available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PnidMarkDialog: React.FC<PnidMarkDialogProps> = ({
  kind,
  bboxes,
  pnidNoDefault,
  activeEquipment,
  activeNode,
  equipmentInstruments = [],
  nodeInstruments = [],
  directNodeInstruments = [],
  nodes = [],
  equipments = [],
  instruments = [],
  aiData = null,
  initialValues,
  diagramMode = 'pid',
  onSubmit,
  onCancel,
  isMarkingExisting = false,
  onContinueDrawing,
  onClearPath,
  onRedrawPath,
  onEditInstrument,
  onUnattachInstrument,
  onEditEquipmentInstrument,
  onUnattachEquipmentInstrument,
  onReorderInstruments,
  onAutoSequence,
  onAutoAddBranches,
  branchCandidates = [],
  onConfirmBranchCandidate,
  onIgnoreBranchCandidate,
  draftAttachedAnnotationIds = [],
  templateAnnotations = [],
  onSaveAttachments,
  onDetachAnnotation,
  onHighlightEntity,
  onMarkInstrument,
  onMarkReviewed,
  onSplitCompoundEquipment,
  compoundSplitInheritedType = null,
  currentDocument = null,
  allDocuments = [],
  linkedDocumentStates = {},
  onLoadLinkedDocumentState,
  onOpenLinkedDocument,
  readOnly = false,
}) => {
  const [values, setValues] = useState<Record<string, any>>({});
  const isPfdMode = diagramMode === 'pfd';
  const pfdFormKind = isPfdMode ? (kind === 'equipment' ? 'pfd_equipment' : 'pfd_node') : null;
  const pfdSchema = useFormSchema(pfdFormKind);

  // Supplies the type options, and judges a stored value against the reviewed
  // list so an unreviewed one can be re-classified.
  const equipmentTypes = useEquipmentTypes();
  // Only a loaded taxonomy can prove a value is outside it. While the fetch is
  // pending or failed, every key looks unknown, and marking a valid one as
  // unreviewed would state something false about stored data.
  const isUnreviewedEquipmentType =
    equipmentTypes.loaded &&
    Boolean(values.equipment_type) &&
    !equipmentTypes.types.some((item) => item.key === values.equipment_type);

  // What the register knows about this drawing's equipment. Only the equipment
  // form reads it, so nothing is fetched while a node or a PFD is being edited.
  const registerContextDocId =
    !isPfdMode && kind === 'equipment' ? currentDocument?.id ?? null : null;
  const registerContext = useRegisterContext(registerContextDocId);
  const invalidateRegisterContext = useInvalidateRegisterContext();
  const editedEquipmentId: string =
    values._markExistingId || values.id || activeEquipment?.id || '';
  const elementRegisterEntry = getEquipmentRegisterContext(
    registerContext.context,
    editedEquipmentId,
  );
  // The tag this equipment was opened on, which is the one the element's own
  // register entry answers for.
  const openedEquipmentNoRef = useRef('');
  // The tag the mark on the form answers for. It starts as the opened tag and
  // moves with each settle, so leaving the field without changing the tag is
  // told apart from renaming the element onto another asset.
  const markedTagRef = useRef('');
  /**
   * The tag the reviewer has settled on, once it is no longer the one the dialog
   * opened with. The register is then asked about that tag instead of about the
   * element, so choosing a tag another drawing owns names that drawing before
   * anything is saved. Reverting to the opened tag drops back to the element.
   */
  const [settledEquipmentTag, setSettledEquipmentTag] = useState<string | null>(null);
  const tagRegisterContext = useTagRegisterContext(registerContextDocId, settledEquipmentTag);
  const handleEquipmentTagSettled = (tag: string) => {
    const settled = tag.trim();
    const openedTag = openedEquipmentNoRef.current.trim();
    setSettledEquipmentTag(settled && settled !== openedTag ? settled : null);
    // The reviewer left the field on the tag the mark already answers for, so
    // their answer stands — leaving a field is not restating it.
    if (normalizeTag(settled) === normalizeTag(markedTagRef.current)) return;
    markedTagRef.current = settled;
    // A primary mark approves "this drawing defines tag X", so settling on a
    // different tag leaves the claim answering for an asset nobody approved it
    // for. Drop it: claiming the new tag is a deliberate re-tick, and the panel
    // beside the box already names whichever drawing holds that tag now.
    // Settling back on the opened tag only clears the answer, and the element's
    // own stored mark takes over again through the resync effect below.
    primaryMarkTouchedRef.current = false;
    if (normalizeTag(settled) === normalizeTag(openedTag)) return;
    carriedPrimarySourceRef.current = false;
    setValues((prev) => ({ ...prev, is_primary_source: undefined }));
  };
  /**
   * The element is pointed at a tag other than the one it opened on. Its stored
   * mark answers for the tag it opened on, so nothing may adopt that mark while
   * this holds.
   */
  const equipmentTagRenamed =
    settledEquipmentTag !== null &&
    normalizeTag(settledEquipmentTag) !== normalizeTag(openedEquipmentNoRef.current);
  // While a settled tag is in force it is that tag's answer the panel reports,
  // including how far the read has got.
  const readingTagRegisterEntry = settledEquipmentTag !== null;
  const equipmentRegisterEntry = readingTagRegisterEntry
    ? tagRegisterContext.entry
    : elementRegisterEntry;
  const registerLoading = readingTagRegisterEntry
    ? tagRegisterContext.loading
    : registerContext.loading;
  const registerError = readingTagRegisterEntry
    ? tagRegisterContext.error
    : registerContext.error;
  // The account this drawing belongs to is what scopes the Domain picker, and the
  // drawing's own plant owns an asset that names no owner.
  const drawingDomainId = currentDocument?.domain_id ?? '';
  const storedOwnerDomainId: string = values.owner_domain_id ?? '';

  // The mark is offered on saved equipment only — an unsaved mark has no asset
  // to be primary for.
  const showPrimarySourceBlock =
    !isPfdMode &&
    kind === 'equipment' &&
    Boolean(editedEquipmentId) &&
    (Boolean(registerContext.context) || registerLoading || Boolean(registerError));
  // Whether the reviewer has answered the checkbox in this dialog session. Until
  // they have, the register's mark is the newer statement — another drawing may
  // have taken the asset over since this one loaded.
  const primaryMarkTouchedRef = useRef(false);
  const isPrimarySourceChecked = resolvePrimaryMark(equipmentRegisterEntry, {
    liveToggle: primaryMarkTouchedRef.current ? values.is_primary_source === true : null,
    wizardMark: values.is_primary_source === true,
  });
  const canonicalFieldsLocked = ownedByAnotherDrawing(
    equipmentRegisterEntry,
    isPrimarySourceChecked,
  );
  // The checkbox is on screen and can be answered, so it governs the fields it
  // owns. While the register is still being read there is nothing to tick, and
  // locking the fields would trap the reviewer behind a control that is absent.
  const primaryMarkOffered = showPrimarySourceBlock && !registerLoading && !registerError;
  /**
   * Type, sub-type and description describe the asset itself, so only the drawing a
   * reviewer approved as its primary P&ID may state them. Publishing the wrong
   * asset costs more than publishing none, so the tick is required even when
   * this is the only drawing that shows the asset.
   */
  const assetFieldsLocked =
    canonicalFieldsLocked ||
    (primaryMarkOffered &&
      !isPrimarySourceChecked &&
      !equipmentRegisterEntry?.this_file_is_primary);
  const lockedEquipmentType = canonicalFieldsLocked
    ? equipmentRegisterEntry?.asset_type ?? ''
    : '';
  const lockedTypeIsOffList =
    Boolean(lockedEquipmentType) &&
    equipmentTypes.loaded &&
    !equipmentTypes.types.some((item) => item.key === lockedEquipmentType);
  // The sub-type list belongs to the type on show, which is the register's own
  // while the canonical fields are locked to another drawing.
  const equipmentSubtypeOptions = equipmentTypes.subtypesFor(
    canonicalFieldsLocked ? lockedEquipmentType : (values.equipment_type ?? ''),
  );

  // The mark is only unset for equipment that carried it; equipment that never
  // did is left alone rather than told it is not primary.
  const carriedPrimarySourceRef = useRef(false);
  // The register can be the only place the mark is recorded, for equipment
  // whose own field predates it, so its answer counts as having carried it.
  useEffect(() => {
    if (equipmentRegisterEntry?.this_file_is_primary) carriedPrimarySourceRef.current = true;
  }, [equipmentRegisterEntry]);

  // Prefer _targetNodeId from initialValues over the activeNode prop to avoid
  // a stale-state race where activeNode hasn't caught up when the dialog opens.
  const effectiveNode = useMemo(() => {
    const targetNodeId = initialValues?._targetNodeId;
    if (targetNodeId && nodes) {
      const found = nodes.find(n => n.id === targetNodeId);
      if (found) return found;
    }
    return activeNode ?? null;
  }, [initialValues?._targetNodeId, nodes, activeNode]);
  type NodeQuickStep = 'line_no' | 'source_ref' | 'source_pnid' | 'dest_ref' | 'dest_pnid' | 'submit';
  const [nodeQuickStep, setNodeQuickStep] = useState<NodeQuickStep>('line_no');
  const nodeLineNoRef = React.useRef<HTMLInputElement>(null);
  const nodeSourcePnidRef = React.useRef<HTMLInputElement>(null);
  const nodeDestPnidRef = React.useRef<HTMLInputElement>(null);
  const nodeSubmitRef = React.useRef<HTMLButtonElement>(null);

  // Instrument sequence management state (for kind === 'node')
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [confirmUnattach, setConfirmUnattach] = useState<string | null>(null);
  const [isAutoSequencing, setIsAutoSequencing] = useState(false);
  const [isDetectingBranches, setIsDetectingBranches] = useState(false);
  const [isSavingAttachments, setIsSavingAttachments] = useState(false);
  // Equipment instrument management state (for kind === 'equipment')
  const [confirmUnattachEquip, setConfirmUnattachEquip] = useState<string | null>(null);
  const [reviewingKey, setReviewingKey] = useState<string | null>(null);

  // Line Properties section (kind === 'node') — expanded if any field is non-default.
  const [linePropsExpanded, setLinePropsExpanded] = useState(false);

  const nodeQuickEnabled = !isPfdMode && kind === 'node' && Boolean(values._from_line_wizard);

  const nodeQuickSteps = useMemo<NodeQuickStep[]>(() => {
    return ['line_no', 'source_ref', 'source_pnid', 'dest_ref', 'dest_pnid', 'submit'];
  }, []);

  const computeInitialNodeQuickStep = (v: Record<string, any>): NodeQuickStep => {
    const steps: NodeQuickStep[] = ['line_no', 'source_ref', 'source_pnid', 'dest_ref', 'dest_pnid'];
    for (const step of steps) {
      if (step === 'line_no' && !String(v.line_no ?? '').trim()) return 'line_no';
      if (step === 'source_ref' && !String(v.source_ref ?? '').trim()) return 'source_ref';
      if (step === 'source_pnid' && !String(v.source_pnid ?? '').trim()) return 'source_pnid';
      if (step === 'dest_ref' && !String(v.dest_ref ?? '').trim()) return 'dest_ref';
      if (step === 'dest_pnid' && !String(v.dest_pnid ?? '').trim()) return 'dest_pnid';
    }
    return 'submit';
  };

  const nodeGoNext = useCallback(() => {
    if (!nodeQuickEnabled) return;
    const idx = nodeQuickSteps.indexOf(nodeQuickStep);
    const next = nodeQuickSteps[Math.min(idx + 1, nodeQuickSteps.length - 1)];
    if (next) setNodeQuickStep(next);
  }, [nodeQuickEnabled, nodeQuickStep, nodeQuickSteps]);

  const nodeGoPrev = useCallback(() => {
    if (!nodeQuickEnabled) return;
    const idx = nodeQuickSteps.indexOf(nodeQuickStep);
    const prev = nodeQuickSteps[Math.max(idx - 1, 0)];
    if (prev) setNodeQuickStep(prev);
  }, [nodeQuickEnabled, nodeQuickStep, nodeQuickSteps]);

  useEffect(() => {
    const nextValues = initialValues ? { ...initialValues } : {};
    if (diagramMode === 'pfd') {
      const sourceDetails = nextValues.details && typeof nextValues.details === 'object' ? nextValues.details : {};
      const sourceLineSpec = nextValues.line_spec && typeof nextValues.line_spec === 'object' ? nextValues.line_spec : {};
      for (const [key, value] of Object.entries(sourceDetails)) {
        if (nextValues[`details.${key}`] === undefined) nextValues[`details.${key}`] = value;
      }
      for (const [key, value] of Object.entries(sourceLineSpec)) {
        if (nextValues[`line_spec.${key}`] === undefined) nextValues[`line_spec.${key}`] = value;
      }
    }
    setValues(nextValues);
    carriedPrimarySourceRef.current = nextValues.is_primary_source === true;
    primaryMarkTouchedRef.current = false;
    openedEquipmentNoRef.current =
      typeof nextValues.equipment_no === 'string' ? nextValues.equipment_no : '';
    markedTagRef.current = openedEquipmentNoRef.current;
    setSettledEquipmentTag(null);
    if (kind === 'node' && nextValues._from_line_wizard) {
      setNodeQuickStep(computeInitialNodeQuickStep(nextValues));
    } else {
      setNodeQuickStep('line_no');
    }
    // Expand Line Properties if any non-default field is set on the active node.
    if (kind === 'node') {
      const hasNonDefault =
        (nextValues.installation_type && nextValues.installation_type !== 'above_ground_main') ||
        nextValues.is_jacketed === true ||
        (nextValues.tracing_type && nextValues.tracing_type !== 'none') ||
        (nextValues.flow_profile && nextValues.flow_profile !== 'standard') ||
        Boolean(nextValues.slope_ratio) ||
        nextValues.is_two_phase === true ||
        (nextValues.flow_direction && nextValues.flow_direction !== 'forward') ||
        Boolean(nextValues.primary_direction);
      setLinePropsExpanded(Boolean(hasNonDefault));
    }
  }, [initialValues, kind, diagramMode]);

  /**
   * Take the register's mark for this element as the form's own. The saved mark
   * is what the drawing holds, so the field states a claim only while one is
   * stored, and spelling out a release is left to the reviewer's untick.
   */
  const adoptSavedPrimaryMark = (savedMark: boolean) => {
    carriedPrimarySourceRef.current = savedMark;
    setValues((prev) => ({ ...prev, is_primary_source: savedMark ? true : undefined }));
  };

  // The register is the current word on what this drawing's annotation holds, so
  // a form the reviewer has not answered follows it: a sibling drawing that took
  // the asset over released this element's mark, and a save from here must carry
  // that release rather than the claim the dialog was seeded with. It is the
  // element's own entry that answers, since that is the mark the save writes —
  // a tag the reviewer is trying out is reported, not adopted.
  //
  // A renamed element is the one case the stored mark cannot answer: it approves
  // the tag the dialog opened on, not the one now typed. Reverting the tag ends
  // the rename and this restores the element's own stored state.
  useEffect(() => {
    if (primaryMarkTouchedRef.current || equipmentTagRenamed) return;
    const savedMark = elementRegisterEntry?.this_drawing_mark;
    if (savedMark === undefined || savedMark === null) return;
    adoptSavedPrimaryMark(savedMark);
    // These dependencies are picked by hand. `adoptSavedPrimaryMark` stays out:
    // it is rebuilt on every render and calls `setValues` with a new object, so
    // listing it would re-run this effect for as long as the dialog is open.
  }, [
    editedEquipmentId,
    equipmentTagRenamed,
    elementRegisterEntry?.this_drawing_mark,
  ]);

  useEffect(() => {
    if (!nodeQuickEnabled) return;
    const focusLater = (el: HTMLElement | null) => {
      if (!el) return;
      setTimeout(() => (el as HTMLElement).focus(), 0);
    };

    if (nodeQuickStep === 'line_no') {
      focusLater(nodeLineNoRef.current);
      return;
    }
    if (nodeQuickStep === 'source_pnid') {
      focusLater(nodeSourcePnidRef.current);
      return;
    }
    if (nodeQuickStep === 'dest_pnid') {
      focusLater(nodeDestPnidRef.current);
      return;
    }
    if (nodeQuickStep === 'submit') {
      focusLater(nodeSubmitRef.current);
    }
  }, [nodeQuickEnabled, nodeQuickStep]);

  useEffect(() => {
    if (values.contextual_type) return;
    const inferred = inferDefaultContextualType(kind, values);
    if (!inferred) return;
    setValues((prev) => ({ ...prev, contextual_type: inferred }));
  }, [kind, values.instrument_text, values.subtype, values.instrument_type, values.contextual_type]);

  const title = useMemo(() => {
    const prefix = isMarkingExisting ? 'Update' : 'Mark';
    switch (kind) {
      case 'equipment':
        return `${prefix} Equipment`;
      case 'node':
        return `${prefix} Connection (Line)`;
      default:
        return prefix;
    }
  }, [kind, isMarkingExisting]);

  // Track field change counts for analytics. The dialog mounts once per opening,
  // so the refs' initial values are already the per-opening starting point.
  const fieldChangeCountRef = useRef(0);
  const fieldsChangedRef = useRef<Set<string>>(new Set());

  // Stable custom-renderer maps for the PFD forms. Each entry delegates to a ref
  // whose `.current` is reassigned below (after setField / render helpers are defined),
  // so the renderer component identities never change — inputs keep focus on re-render.
  const pfdSourceRefRenderRef = useRef<(p: { value: any; readOnly?: boolean }) => React.ReactNode>(() => null);
  const pfdDestRefRenderRef = useRef<(p: { value: any; readOnly?: boolean }) => React.ReactNode>(() => null);
  const pfdConnectedEqRenderRef = useRef<() => React.ReactNode>(() => null);
  const pfdLinkedPnidRenderRef = useRef<() => React.ReactNode>(() => null);
  const pfdLinkedStreamRenderRef = useRef<() => React.ReactNode>(() => null);
  const pfdNodeCustomRenderers = useMemo<CustomRendererMap>(() => ({
    source_ref: (p) => pfdSourceRefRenderRef.current(p),
    dest_ref: (p) => pfdDestRefRenderRef.current(p),
    linked_pfd_streams: () => pfdLinkedStreamRenderRef.current(),
  }), []);
  // Read through a ref so the renderer map below keeps its empty dep list.
  const currentDocIdRef = useRef<string | null>(null);
  currentDocIdRef.current = currentDocument?.id ?? null;
  const pfdEquipmentCustomRenderers = useMemo<CustomRendererMap>(() => ({
    // The PFD equipment tag is the same plant vocabulary as the P&ID one, so the
    // schema's tag field renders the suggesting input rather than a plain text box.
    equipment_no: ({ field, value, onChange, readOnly: fieldReadOnly }) => (
      <label style={{ display: 'block', fontSize: 12 }}>
        <span style={{ fontWeight: 500 }}>
          {field.label}{field.required ? ' (required)' : ' (optional)'}
        </span>
        <TagAutocompleteInput
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          docId={currentDocIdRef.current}
          kind="equipment"
          placeholder={field.placeholder}
          disabled={fieldReadOnly}
          required={field.required}
          style={{ marginTop: 4 }}
        />
      </label>
    ),
    connected_equipments: () => pfdConnectedEqRenderRef.current(),
    linked_pnids: () => pfdLinkedPnidRenderRef.current(),
  }), []);

  useEffect(() => {
    if (!onLoadLinkedDocumentState) return;
    const rows = [
      ...(Array.isArray(values.linked_pnids) ? values.linked_pnids : []),
      ...(Array.isArray(values.linked_pfds) ? values.linked_pfds : []),
      ...(Array.isArray(values.linked_pfd_streams) ? values.linked_pfd_streams : []),
    ];
    for (const row of rows) {
      const targetFileId = row?.pnid_file_id || row?.pfd_file_id;
      if (targetFileId && !linkedDocumentStates[targetFileId]) {
        void onLoadLinkedDocumentState(targetFileId);
      }
    }
  }, [
    kind,
    linkedDocumentStates,
    onLoadLinkedDocumentState,
    values.linked_pfds,
    values.linked_pfd_streams,
    values.linked_pnids,
  ]);

  const setField = (key: string, value: any) => {
    fieldChangeCountRef.current += 1;
    fieldsChangedRef.current.add(key);
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  /* The new type brings its own sub-type list, and a stored sub-type it does not
     offer would otherwise be saved against a list that no longer holds it. */
  const changeEquipmentType = (nextType: string) => {
    setField('equipment_type', nextType);
    if (values.subtype && !equipmentTypes.subtypesFor(nextType).includes(values.subtype)) {
      setField('subtype', null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    onSubmit({
      ...values,
      _field_change_count: fieldChangeCountRef.current,
      _fields_changed: Array.from(fieldsChangedRef.current),
    });
  };

  const regionInfoLines = bboxes.map(
    (region) =>
      `X ${Math.round(region.x1)}-${Math.round(region.x2)}, Y ${Math.round(region.y1)}-${Math.round(region.y2)}`,
  );

  const formatLinkStatus = (status?: string | null) => (
    status === 'needs_review' ? 'Needs review' : 'Confirmed'
  );

  const formatStoredText = (value?: string | null) => (
    typeof value === 'string' && value.trim() ? value.trim() : '-'
  );

  const isOpcInstrument = (inst: PnidInstrument): boolean => (
    typeof inst.instrument_type === 'string' && inst.instrument_type.trim().toUpperCase() === 'OPC'
  );

  const getNodeOpcSnapshot = (
    docInstruments: PnidInstrument[],
    nodeId: string | null | undefined,
  ): { instrumentId: string | null; tag: string | null } => {
    if (!nodeId) return { instrumentId: null, tag: null };
    const seen = new Set<string>();
    const candidates = docInstruments
      .filter((inst) => {
        if (!inst.id || seen.has(inst.id) || !isOpcInstrument(inst)) return false;
        seen.add(inst.id);
        const effectiveContext = getInstrumentEffectiveContext(inst, { instruments: docInstruments });
        return effectiveContext.context_type === 'node' && effectiveContext.context_id === nodeId;
      })
      .sort((a, b) => getInstrumentPrimaryLabel(a).localeCompare(getInstrumentPrimaryLabel(b)));
    const selected = candidates[0];
    return selected
      ? { instrumentId: selected.id, tag: getInstrumentPrimaryLabel(selected) || selected.generated_id || null }
      : { instrumentId: null, tag: null };
  };

  const openLinkedDocumentButtonStyle: React.CSSProperties = {
    padding: '4px 8px',
    fontSize: 10,
    color: 'var(--raven-primary-main)',
    backgroundColor: 'var(--raven-state-selected)',
    border: '1px solid var(--raven-primary-light)',
    borderRadius: 3,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  // (Instrument-specific isolation valve, auto-ID, and linked instrument logic removed — now in InstrumentEditor)

  // Recursive value renderer — shows nested dicts/arrays as readable rows rather than raw JSON.
  const formatKey = (k: string) => k.replace(/_/g, ' ');

  const renderValue = (value: any, depth = 0): React.ReactNode => {
    if (value === null || value === undefined || value === '') {
      return <span style={{ color: 'var(--raven-text-disabled)' }}>—</span>;
    }
    if (typeof value === 'boolean') {
      return <span style={{ fontWeight: 500 }}>{value ? 'Yes' : 'No'}</span>;
    }
    if (typeof value === 'number' || typeof value === 'string') {
      return <span style={{ fontWeight: 500 }}>{String(value)}</span>;
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return <span style={{ color: 'var(--raven-text-disabled)' }}>(empty)</span>;
      const allPrimitive = value.every(
        (v) => v === null || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean'
      );
      if (allPrimitive) {
        return <span style={{ fontWeight: 500 }}>{value.map((v) => String(v ?? '—')).join(', ')}</span>;
      }
      if (depth >= 3) {
        return <span style={{ color: 'var(--raven-text-disabled)', fontStyle: 'italic' }}>[{value.length} items]</span>;
      }
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {value.map((v, i) => (
            <div
              key={i}
              style={{
                padding: 4,
                borderLeft: '2px solid var(--raven-divider)',
                paddingLeft: 6,
              }}
            >
              {renderValue(v, depth + 1)}
            </div>
          ))}
        </div>
      );
    }
    // object
    const entries = Object.entries(value as Record<string, any>);
    if (entries.length === 0) return <span style={{ color: 'var(--raven-text-disabled)' }}>(empty)</span>;
    if (depth >= 3) {
      return <span style={{ color: 'var(--raven-text-disabled)', fontStyle: 'italic' }}>{`{${entries.length} fields}`}</span>;
    }
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          gap: '2px 8px',
          padding: depth === 0 ? 0 : 4,
          ...(depth > 0
            ? {
                borderLeft: '2px solid var(--raven-divider)',
                paddingLeft: 6,
              }
            : {}),
        }}
      >
        {entries.map(([k, v]) => (
          <React.Fragment key={k}>
            <span style={{ color: 'var(--raven-text-secondary)' }}>{formatKey(k)}:</span>
            <span>{renderValue(v, depth + 1)}</span>
          </React.Fragment>
        ))}
      </div>
    );
  };

  const renderDictSection = (title: string, data: Record<string, any> | null | undefined, accent: string) => {
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) return null;
    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 6 }}>{title}</div>
        <div
          style={{
            padding: 8,
            background: accent,
            border: '1px solid var(--raven-divider)',
            borderRadius: 6,
            fontSize: 11,
          }}
        >
          {renderValue(data, 0)}
        </div>
      </div>
    );
  };

  // Render dynamic details + equipment_data for equipment
  const renderDetailsFields = () => (
    <>
      {renderDictSection('Design Data (from AI)', values.details, 'var(--raven-surface-tier2)')}
      {renderDictSection('Datasheet', values.equipment_data, 'var(--raven-surface-tier2)')}
    </>
  );

  /**
   * Checking the box claims the asset for this drawing; unchecking releases it,
   * but only for equipment that carried the mark — for the rest the field stays
   * undefined so the save says nothing about primacy.
   *
   * Either answer is the reviewer's own, so from here on it stands over what the
   * register reports, until the dialog reseeds or a refresh resyncs it.
   */
  const setPrimarySource = (checked: boolean) => {
    primaryMarkTouchedRef.current = true;
    if (checked) {
      setField('is_primary_source', true);
      return;
    }
    setField('is_primary_source', carriedPrimarySourceRef.current ? false : undefined);
  };

  /**
   * Which drawing is primary for this asset. Another drawing is a link: it opens
   * that drawing in a new tab with the same equipment already open, so the
   * reviewer can read or change the values it owns without hunting for it.
   */
  const renderPrimaryDrawing = (entry: RegisterEquipmentContext) => {
    const primary = primaryDrawing(entry, isPrimarySourceChecked);
    if (!primary) return 'none';
    if (primary.isThisDrawing) return 'this drawing';
    const label = primary.drawingNo || 'another drawing';
    if (!primary.docId) return label;
    return (
      <a
        href={buildEquipmentEditHref(primary.docId, primary.equipmentId)}
        target="_blank"
        rel="noreferrer"
        style={{ color: 'var(--raven-text-secondary)', fontWeight: 600 }}
      >
        {label}
      </a>
    );
  };

  // The mark that makes this drawing authoritative for the asset, over the
  // register's account of every drawing the asset appears on.
  const renderPrimarySourceBlock = () => {
    if (!showPrimarySourceBlock) return null;

    // Cross-drawing data (Primary/Also-appears-on, and locked fields) is only
    // as fresh as the last fetch, so a reviewer who just edited the asset on
    // another tab can force this drawing to reread the register rather than
    // waiting on a full page reload.
    //
    // Refreshing is a resync, so the saved state wins outright: an unsaved tick
    // is dropped here, and the reread that follows reseeds the form from what
    // the register reports.
    const refreshRegisterContext = () => {
      if (!registerContextDocId) return;
      primaryMarkTouchedRef.current = false;
      // The element's own entry, since that is the mark a save writes — a tag
      // the reviewer is trying out is reported, not adopted. A renamed element
      // has no stored mark that answers for the tag it now carries, so the
      // resync leaves the dropped claim dropped.
      const savedMark = elementRegisterEntry?.this_drawing_mark;
      if (!equipmentTagRenamed && savedMark !== undefined && savedMark !== null) {
        adoptSavedPrimaryMark(savedMark);
      }
      invalidateRegisterContext(registerContextDocId);
    };
    const refreshButton = (
      <button
        type="button"
        onClick={refreshRegisterContext}
        disabled={registerContext.loading}
        style={{
          fontSize: 11,
          color: 'var(--raven-text-secondary)',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: registerContext.loading ? 'default' : 'pointer',
          opacity: registerContext.loading ? 0.5 : 1,
        }}
        onMouseEnter={(e) => {
          if (!registerContext.loading) e.currentTarget.style.color = 'var(--raven-text-primary)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'var(--raven-text-secondary)';
        }}
      >
        ↻ Refresh
      </button>
    );

    // Until the register answers, the checkbox would state a claim nobody has
    // read yet, and the canonical fields would look editable when they may not
    // be — so say which it is instead of showing the control.
    if (registerLoading || registerError) {
      return (
        <div
          style={{
            ...PRIMARY_SOURCE_PANEL_STYLE,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: registerError ? 'var(--raven-warning-text)' : 'var(--raven-text-secondary)',
            }}
          >
            {registerError
              ? `Primary P&ID unavailable — ${registerError}`
              : 'Checking the register for this asset…'}
          </div>
          {refreshButton}
        </div>
      );
    }
    const entry = equipmentRegisterEntry;

    return (
      <div style={PRIMARY_SOURCE_PANEL_STYLE}>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{refreshButton}</div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12 }}>
          <input
            type="checkbox"
            checked={isPrimarySourceChecked}
            onChange={(e) => setPrimarySource(e.target.checked)}
            disabled={readOnly}
            /* accent-color: without it a native checkbox paints in the OS accent
               (pink on some Macs), which read as off-brand against the green app. */
            style={{ marginTop: 2, accentColor: 'var(--raven-primary-main)' }}
          />
          <span>
            <span style={{ fontWeight: 600, color: 'var(--raven-text-secondary)' }}>Primary P&amp;ID</span>
            <div style={{ color: 'var(--raven-text-secondary)', marginTop: 2 }}>
              This drawing owns the tag, type and description of the register asset.
            </div>
          </span>
        </label>
        {entry && (
          <div style={{ fontSize: 11, color: 'var(--raven-text-secondary)', marginTop: 6 }}>
            Primary: {renderPrimaryDrawing(entry)}
          </div>
        )}
        {entry && entry.appearances.some((a) => a.doc_id && a.doc_id !== registerContextDocId) && (
          <div style={{ fontSize: 11, color: 'var(--raven-text-secondary)', marginTop: 4 }}>
            Also appears on:{' '}
            {entry.appearances
              .filter((a) => a.doc_id && a.doc_id !== registerContextDocId)
              .map((a, i) => (
                <span key={a.source_file_id}>
                  {i > 0 && ', '}
                  <a
                    href={buildEquipmentEditHref(a.doc_id as string, a.equipment_id)}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: 'var(--raven-text-secondary)' }}
                  >
                    {a.drawing_no || 'untitled drawing'}
                  </a>
                </span>
              ))}
          </div>
        )}
        {canonicalFieldsLocked && entry && (
          <div style={{ fontSize: 11, color: 'var(--raven-warning-text)', marginTop: 4 }}>
            Type, sub-type and description show the values from {describePrimaryDrawing(entry)}.
            Mark this drawing primary to edit them here.
          </div>
        )}
      </div>
    );
  };

  // Connected Equipment editor (bidirectional peer links)
  const renderConnectedEquipmentSection = () => {
    const currentId = values._markExistingId || values.id || activeEquipment?.id || '';
    const connections: Array<{ equipment_id: string; role: string }> = Array.isArray(
      values.connected_equipments
    )
      ? values.connected_equipments
      : [];

    const peerOptions: AutocompleteOption[] = equipments
      .filter((eq) => eq.id && eq.id !== currentId)
      .map((eq) => ({
        value: eq.id,
        label: eq.equipment_no || 'Unnamed equipment',
      }));

    const setConnections = (next: Array<{ equipment_id: string; role: string }>) => {
      setField('connected_equipments', next);
    };

    const updateRow = (
      idx: number,
      patch: Partial<{ equipment_id: string; role: string }>
    ) => {
      setConnections(connections.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
    };

    const removeRow = (idx: number) => {
      setConnections(connections.filter((_, i) => i !== idx));
    };

    const addRow = () => {
      setConnections([...connections, { equipment_id: '', role: '' }]);
    };

    return (
      <div style={{ marginTop: 4 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 6,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: 'var(--raven-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Connected Equipment ({connections.length})
          </div>
          <button
            type="button"
            onClick={addRow}
            disabled={readOnly}
            style={{
              padding: '2px 8px',
              fontSize: 9,
              fontWeight: 500,
              color: 'var(--raven-text-secondary)',
              backgroundColor: 'var(--raven-state-selected)',
              border: '1px solid var(--raven-primary-light)',
              borderRadius: 4,
              cursor: readOnly ? 'not-allowed' : 'pointer',
              opacity: readOnly ? 0.55 : 1,
            }}
            title="Add a peer equipment connection"
          >
            + Add connection
          </button>
        </div>

        {connections.length === 0 ? (
          <div
            style={{
              padding: 10,
              textAlign: 'center',
              color: 'var(--raven-text-disabled)',
              fontSize: 11,
              backgroundColor: 'var(--raven-surface-tier2)',
              borderRadius: 6,
            }}
          >
            No connections. Bidirectionally mirrored to the peer on save.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {connections.map((c, idx) => (
              <div
                key={idx}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr auto',
                  gap: 6,
                  alignItems: 'center',
                  padding: 6,
                  background: 'var(--raven-surface-tier2)',
                  border: '1px solid var(--raven-divider)',
                  borderRadius: 6,
                }}
              >
                <AutocompleteDropdown
                  value={c.equipment_id}
                  onChange={(val) => updateRow(idx, { equipment_id: val })}
                  options={peerOptions}
                  placeholder="Select equipment..."
                  allowCustom={false}
                  displayLabel
                />
                <input
                  type="text"
                  value={c.role}
                  onChange={(e) => updateRow(idx, { role: e.target.value })}
                  placeholder="Role (e.g. feeds, drains to)"
                  style={{
                    padding: 6,
                    fontSize: 11,
                    border: '1px solid var(--raven-divider)',
                    borderRadius: 4,
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  disabled={readOnly}
                  style={{
                    padding: '4px 8px',
                    fontSize: 11,
                    color: 'var(--raven-text-secondary)',
                    background: 'white',
                    border: '1px solid var(--raven-divider)',
                    borderRadius: 4,
                    cursor: readOnly ? 'not-allowed' : 'pointer',
                    opacity: readOnly ? 0.55 : 1,
                  }}
                  title="Remove connection"
                  aria-label="Remove connection"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /** The compound-tag warning band, and the split editor it opens.
   *
   *  Offered only where the parent supplied a split handler — an existing P&ID
   *  equipment. The editor itself is shared with the review table and the plant
   *  hierarchy; this panel supplies the drawing's own gate on top of it.
   */
  const renderCompoundSplitSection = () => {
    if (!onSplitCompoundEquipment) return null;
    const compoundTag = String(values.equipment_no ?? '');
    if (!isCompoundTag(compoundTag)) return null;

    return (
      <CompoundSplitEditor
        docId={currentDocument?.id}
        equipmentId={editedEquipmentId}
        tag={compoundTag}
        description={String(values.description ?? '')}
        inheritedType={compoundSplitInheritedType}
        /* The split rewrites the tag, so only the drawing that owns the asset's
           canonical fields may run it. */
        blocked={readOnly || canonicalFieldsLocked}
        blockedReason={
          canonicalFieldsLocked ? 'The primary drawing owns this tag. Split it there.' : null
        }
        onConfirm={onSplitCompoundEquipment}
      />
    );
  };

  const renderEquipmentDocumentLinks = (target: 'pnid' | 'pfd') => {
    const isPnidTarget = target === 'pnid';
    const fieldKey = isPnidTarget ? 'linked_pnids' : 'linked_pfds';
    const rows: Array<LinkedPnidRef | LinkedPfdRef> = Array.isArray(values[fieldKey])
      ? values[fieldKey]
      : [];
    const targetDiagramType = isPnidTarget ? 'pid' : 'pfd';
    const currentPlant = currentDocument?.plant ?? null;
    const docOptions = allDocuments
      .filter((doc) => {
        const docType = doc.diagram_type === 'pfd' ? 'pfd' : 'pid';
        if (doc.id === currentDocument?.id) return false;
        if (docType !== targetDiagramType) return false;
        if (currentPlant && doc.plant && doc.plant !== currentPlant) return false;
        return true;
      })
      .map((doc) => ({
        value: doc.id,
        label: doc.pnid_no || doc.original_filename || doc.filename || doc.id,
      }));

    const getStoredDocumentLabel = (docId: string) => {
      const option = docOptions.find((doc) => doc.value === docId);
      if (option) return option.label;
      const doc = allDocuments.find((candidate) => candidate.id === docId);
      return doc?.pnid_no || doc?.original_filename || doc?.filename || (isPnidTarget ? 'Selected P&ID' : 'Selected PFD');
    };

    const setRows = (next: Array<LinkedPnidRef | LinkedPfdRef>) => {
      setField(fieldKey, next);
    };

    const updateRow = (idx: number, patch: Partial<LinkedPnidRef & LinkedPfdRef>) => {
      setRows(rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
    };

    const addRow = () => {
      if (isPnidTarget) {
        setRows([
          ...rows,
          {
            pnid_file_id: '',
            equipment_id: '',
            status: 'confirmed',
            source: 'manual',
          } as LinkedPnidRef,
        ]);
      } else {
        setRows([
          ...rows,
          {
            pfd_file_id: '',
            equipment_id: '',
            status: 'confirmed',
            source: 'manual',
          } as LinkedPfdRef,
        ]);
      }
    };

    const removeRow = (idx: number) => {
      setRows(rows.filter((_, i) => i !== idx));
    };

    const onDocumentChange = (idx: number, docId: string) => {
      updateRow(idx, {
        ...(isPnidTarget ? { pnid_file_id: docId } : { pfd_file_id: docId }),
        equipment_id: '',
      });
      if (docId && onLoadLinkedDocumentState && !linkedDocumentStates[docId]) {
        void onLoadLinkedDocumentState(docId);
      }
    };

    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            {isPnidTarget ? 'Linked P&IDs' : 'Linked PFDs'} ({rows.length})
          </div>
          <button
            type="button"
            onClick={addRow}
            disabled={readOnly}
            style={{
              padding: '2px 8px',
              fontSize: 9,
              fontWeight: 500,
              color: 'var(--raven-text-secondary)',
              backgroundColor: 'var(--raven-state-selected)',
              border: '1px solid var(--raven-primary-light)',
              borderRadius: 4,
              cursor: readOnly ? 'not-allowed' : 'pointer',
              opacity: readOnly ? 0.55 : 1,
            }}
          >
            + Add link
          </button>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: 10, textAlign: 'center', color: 'var(--raven-text-disabled)', fontSize: 11, backgroundColor: 'var(--raven-surface-tier2)', borderRadius: 6 }}>
            No cross-document equipment links.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rows.map((row, idx) => {
              const targetFileId = isPnidTarget
                ? (row as LinkedPnidRef).pnid_file_id
                : (row as LinkedPfdRef).pfd_file_id;
              const targetState = linkedDocumentStates[targetFileId];
              const equipmentOptions = (targetState?.equipments ?? []).map((eq) => ({
                value: eq.id,
                label: eq.equipment_no || eq.id,
              }));
              const storedDocLabel = targetFileId ? getStoredDocumentLabel(targetFileId) : 'No document selected';
              const docOptionsWithStored =
                targetFileId && !docOptions.some((option) => option.value === targetFileId)
                  ? [{ value: targetFileId, label: `${storedDocLabel} (current)` }, ...docOptions]
                  : docOptions;
              const storedEquipment = targetState?.equipments.find((eq) => eq.id === row.equipment_id);
              const storedEquipmentLabel = row.equipment_id
                ? storedEquipment?.equipment_no || 'Selected equipment'
                : 'No equipment selected';
              const equipmentOptionsWithStored =
                row.equipment_id && !equipmentOptions.some((option) => option.value === row.equipment_id)
                  ? [{ value: row.equipment_id, label: `${storedEquipmentLabel} (current)` }, ...equipmentOptions]
                  : equipmentOptions;
              return (
                <div
                  key={idx}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    padding: 6,
                    background: 'var(--raven-surface-tier2)',
                    border: '1px solid var(--raven-divider)',
                    borderRadius: 6,
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <select
                      value={targetFileId}
                      onChange={(e) => onDocumentChange(idx, e.target.value)}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 0 }}
                    >
                      <option value="">{isPnidTarget ? 'Select P&ID' : 'Select PFD'}</option>
                      {docOptionsWithStored.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <select
                      value={row.equipment_id}
                      onChange={(e) => updateRow(idx, { equipment_id: e.target.value })}
                      disabled={readOnly || !targetFileId}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 0 }}
                    >
                      <option value="">{targetFileId && !targetState ? 'Loading...' : 'Select equipment'}</option>
                      {equipmentOptionsWithStored.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                    <select
                      value={row.status ?? 'confirmed'}
                      onChange={(e) => updateRow(idx, { status: e.target.value as 'confirmed' | 'needs_review' })}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 110 }}
                    >
                      <option value="confirmed">Confirmed</option>
                      <option value="needs_review">Needs review</option>
                    </select>
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      {targetFileId && row.equipment_id && onOpenLinkedDocument && (
                        <button
                          type="button"
                          onClick={() => onOpenLinkedDocument({ docId: targetFileId, targetEquipmentId: row.equipment_id, newTab: true })}
                          style={openLinkedDocumentButtonStyle}
                        >
                          Open in new tab
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        disabled={readOnly}
                        style={{
                          padding: '4px 8px',
                          fontSize: 11,
                          color: 'var(--raven-text-secondary)',
                          background: 'white',
                          border: '1px solid var(--raven-divider)',
                          borderRadius: 4,
                          cursor: readOnly ? 'not-allowed' : 'pointer',
                          opacity: readOnly ? 0.55 : 1,
                        }}
                        title="Remove link"
                        aria-label="Remove link"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 5,
                      paddingTop: 2,
                      fontSize: 11,
                      color: 'var(--raven-text-secondary)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <div><strong>{isPnidTarget ? 'Linked P&ID' : 'Linked PFD'}:</strong> {storedDocLabel}</div>
                    <div><strong>Equipment:</strong> {storedEquipmentLabel}</div>
                    <div><strong>Status:</strong> {formatLinkStatus(row.status)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderPfdStreamDocumentLinks = () => {
    const rows: PnidLinkedPfdStreamRef[] = Array.isArray(values.linked_pfd_streams)
      ? values.linked_pfd_streams
      : [];
    const currentPlant = currentDocument?.plant ?? null;
    const docOptions = allDocuments
      .filter((doc) => {
        const docType = doc.diagram_type === 'pfd' ? 'pfd' : 'pid';
        if (doc.id === currentDocument?.id) return false;
        if (docType !== 'pfd') return false;
        if (currentPlant && doc.plant && doc.plant !== currentPlant) return false;
        return true;
      })
      .map((doc) => ({
        value: doc.id,
        label: doc.pnid_no || doc.original_filename || doc.filename || doc.id,
      }));

    const getStoredPfdLabel = (row: PnidLinkedPfdStreamRef) => {
      const option = docOptions.find((doc) => doc.value === row.pfd_file_id);
      if (option) return option.label;
      const doc = allDocuments.find((candidate) => candidate.id === row.pfd_file_id);
      return doc?.pnid_no || row.pfd_no || doc?.original_filename || doc?.filename || 'Selected PFD';
    };

    const sourceOpcSnapshot = getNodeOpcSnapshot(
      [...equipmentInstruments, ...nodeInstruments, ...directNodeInstruments],
      effectiveNode?.id,
    );

    const sourceSnapshotPatch = (): Partial<PnidLinkedPfdStreamRef> => ({
      source_line_no_snapshot: effectiveNode?.line_no ?? null,
      source_opc_instrument_id: sourceOpcSnapshot.instrumentId,
      source_opc_tag: sourceOpcSnapshot.tag,
    });

    const setRows = (next: PnidLinkedPfdStreamRef[]) => {
      setField('linked_pfd_streams', next);
    };

    const updateRow = (idx: number, patch: Partial<PnidLinkedPfdStreamRef>) => {
      setRows(rows.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
    };

    const addRow = () => {
      setRows([
        ...rows,
        {
          pfd_file_id: '',
          pfd_no: null,
          node_id: '',
          source_line_no_snapshot: effectiveNode?.line_no ?? null,
          target_line_no_snapshot: null,
          source_opc_instrument_id: sourceOpcSnapshot.instrumentId,
          source_opc_tag: sourceOpcSnapshot.tag,
          target_opc_instrument_id: null,
          target_opc_tag: null,
          endpoint_role: 'dest_pnid',
          direction: 'source_to_dest',
          status: 'confirmed',
          source: 'manual',
        },
      ]);
    };

    const removeRow = (idx: number) => {
      setRows(rows.filter((_, i) => i !== idx));
    };

    const onDocumentChange = (idx: number, docId: string) => {
      const selectedDoc = allDocuments.find((doc) => doc.id === docId);
      updateRow(idx, {
        ...sourceSnapshotPatch(),
        pfd_file_id: docId,
        pfd_no: selectedDoc?.pnid_no || selectedDoc?.original_filename || selectedDoc?.filename || null,
        node_id: '',
        target_line_no_snapshot: null,
        target_opc_instrument_id: null,
        target_opc_tag: null,
      });
      if (docId && onLoadLinkedDocumentState && !linkedDocumentStates[docId]) {
        void onLoadLinkedDocumentState(docId);
      }
    };

    const onStreamChange = (idx: number, nodeId: string) => {
      const row = rows[idx];
      const targetState = row?.pfd_file_id ? linkedDocumentStates[row.pfd_file_id] : null;
      const targetNode = targetState?.nodes.find((node) => node.id === nodeId);
      const targetOpcSnapshot = getNodeOpcSnapshot(targetState?.instruments ?? [], nodeId);
      updateRow(idx, {
        ...sourceSnapshotPatch(),
        node_id: nodeId,
        target_line_no_snapshot: targetNode?.line_no ?? null,
        target_opc_instrument_id: targetOpcSnapshot.instrumentId,
        target_opc_tag: targetOpcSnapshot.tag,
      });
    };

    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Linked PFD Streams ({rows.length})
          </div>
          <button
            type="button"
            onClick={addRow}
            disabled={readOnly}
            style={{
              padding: '2px 8px',
              fontSize: 9,
              fontWeight: 500,
              color: 'var(--raven-text-secondary)',
              backgroundColor: 'var(--raven-state-selected)',
              border: '1px solid var(--raven-primary-light)',
              borderRadius: 4,
              cursor: readOnly ? 'not-allowed' : 'pointer',
              opacity: readOnly ? 0.55 : 1,
            }}
          >
            + Add stream
          </button>
        </div>

        {rows.length === 0 ? (
          <div style={{ padding: 10, textAlign: 'center', color: 'var(--raven-text-disabled)', fontSize: 11, backgroundColor: 'var(--raven-surface-tier2)', borderRadius: 6 }}>
            No cross-PFD stream links.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rows.map((row, idx) => {
              const targetState = linkedDocumentStates[row.pfd_file_id];
              const streamOptions = (targetState?.nodes ?? [])
                .slice()
                .sort((a, b) => a.node_no - b.node_no)
                .map((node) => ({
                  value: node.id,
                  label: node.line_no || `Stream ${node.node_no}`,
                }));
              const storedPfdLabel = row.pfd_file_id ? getStoredPfdLabel(row) : 'No PFD selected';
              const docOptionsWithStored =
                row.pfd_file_id && !docOptions.some((option) => option.value === row.pfd_file_id)
                  ? [{ value: row.pfd_file_id, label: `${storedPfdLabel} (current)` }, ...docOptions]
                  : docOptions;
              const storedStream = targetState?.nodes.find((node) => node.id === row.node_id);
              const storedStreamLabel = row.node_id
                ? storedStream?.line_no || row.target_line_no_snapshot || (storedStream ? `Stream ${storedStream.node_no}` : 'Selected stream')
                : 'No stream selected';
              const targetOpcSnapshot = getNodeOpcSnapshot(targetState?.instruments ?? [], row.node_id);
              const displaySourceOpcTag = row.source_opc_tag || sourceOpcSnapshot.tag;
              const displayTargetOpcTag = row.target_opc_tag || targetOpcSnapshot.tag;
              const streamOptionsWithStored =
                row.node_id && !streamOptions.some((option) => option.value === row.node_id)
                  ? [{ value: row.node_id, label: `${storedStreamLabel} (current)` }, ...streamOptions]
                  : streamOptions;
              return (
                <div
                  key={idx}
                  style={{
                    padding: 6,
                    background: 'var(--raven-surface-tier2)',
                    border: '1px solid var(--raven-divider)',
                    borderRadius: 6,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                  }}
                >
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                    <select
                      value={row.pfd_file_id}
                      onChange={(e) => onDocumentChange(idx, e.target.value)}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 0 }}
                    >
                      <option value="">Select PFD</option>
                      {docOptionsWithStored.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <select
                      value={row.node_id}
                      onChange={(e) => onStreamChange(idx, e.target.value)}
                      disabled={readOnly || !row.pfd_file_id}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4, minWidth: 0 }}
                    >
                      <option value="">{row.pfd_file_id && !targetState ? 'Loading...' : 'Select stream'}</option>
                      {streamOptionsWithStored.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                    <select
                      value={row.endpoint_role}
                      onChange={(e) => updateRow(idx, { endpoint_role: e.target.value as 'source_pnid' | 'dest_pnid' })}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4 }}
                    >
                      <option value="source_pnid">Source P&ID</option>
                      <option value="dest_pnid">Destination P&ID</option>
                    </select>
                    <select
                      value={row.direction}
                      onChange={(e) => updateRow(idx, { direction: e.target.value as 'source_to_dest' | 'dest_to_source' })}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4 }}
                    >
                      <option value="source_to_dest">Source to destination</option>
                      <option value="dest_to_source">Destination to source</option>
                    </select>
                    <select
                      value={row.status ?? 'confirmed'}
                      onChange={(e) => updateRow(idx, { status: e.target.value as 'confirmed' | 'needs_review' })}
                      disabled={readOnly}
                      style={{ padding: 6, fontSize: 11, border: '1px solid var(--raven-divider)', borderRadius: 4 }}
                    >
                      <option value="confirmed">Confirmed</option>
                      <option value="needs_review">Needs review</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)' }}>{formatLinkStatus(row.status)}</div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {row.pfd_file_id && row.node_id && onOpenLinkedDocument && (
                        <button
                          type="button"
                          onClick={() => onOpenLinkedDocument({ docId: row.pfd_file_id, targetNodeId: row.node_id, newTab: true })}
                          style={openLinkedDocumentButtonStyle}
                        >
                          Open in new tab
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => removeRow(idx)}
                        disabled={readOnly}
                        style={{ padding: '4px 8px', fontSize: 10, color: 'var(--raven-text-secondary)', background: 'white', border: '1px solid var(--raven-divider)', borderRadius: 3, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }}
                        title="Remove stream link"
                        aria-label="Remove stream link"
                      >
                        x
                      </button>
                    </div>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 5,
                      fontSize: 11,
                      color: 'var(--raven-text-secondary)',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    <div><strong>Linked PFD:</strong> {storedPfdLabel}</div>
                    <div><strong>Stream:</strong> {storedStreamLabel}</div>
                    <div><strong>Source OPC:</strong> {formatStoredText(displaySourceOpcTag)}</div>
                    <div><strong>Target OPC:</strong> {formatStoredText(displayTargetOpcTag)}</div>
                    <div><strong>Status:</strong> {formatLinkStatus(row.status)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render line_spec fields for node
  const renderLineSpecFields = () => {
    const lineSpec = values.line_spec || {};
    if (Object.keys(lineSpec).length === 0) return null;

    return (
      <div style={{ marginTop: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 6 }}>
          Line Specification (from AI)
        </div>
        <div style={{ 
          padding: 8, 
          background: 'var(--raven-surface-tier3)', 
          border: '1px solid var(--raven-primary-light)', 
          borderRadius: 6, 
          fontSize: 11,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8
        }}>
          {lineSpec.size_in && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-surface-tier3)', borderRadius: 4 }}>
              Size: {lineSpec.size_in}"
            </span>
          )}
          {lineSpec.service_code && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-surface-tier3)', borderRadius: 4 }}>
              Service: {lineSpec.service_code}
            </span>
          )}
          {lineSpec.pipe_class && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-surface-tier3)', borderRadius: 4 }}>
              Class: {lineSpec.pipe_class}
            </span>
          )}
          {lineSpec.sequence_no && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-surface-tier3)', borderRadius: 4 }}>
              Seq: {lineSpec.sequence_no}
            </span>
          )}
          {lineSpec.remark && (
            <span style={{ padding: '2px 6px', background: 'var(--raven-warning-bg)', borderRadius: 4 }}>
              ({lineSpec.remark})
            </span>
          )}
        </div>
      </div>
    );
  };

  // Equipment instrument list (for kind === 'equipment')
  const activeEquipmentInstruments = useMemo(() => {
    if (kind !== 'equipment' || !activeEquipment) return [];
    const instrumentState = { instruments: [...equipmentInstruments, ...nodeInstruments] };
    return equipmentInstruments.filter(i => getInstrumentEffectiveContext(i, instrumentState).context_id === activeEquipment.id);
  }, [kind, activeEquipment, equipmentInstruments, nodeInstruments]);

  // Instrument list drag handlers (for kind === 'node')
  const activeNodeInstruments = useMemo(() => {
    if (kind !== 'node' || !effectiveNode) return [];
    const instrumentState = { instruments: [...equipmentInstruments, ...nodeInstruments] };
    return nodeInstruments.filter(i => getInstrumentEffectiveContext(i, instrumentState).context_id === effectiveNode.id);
  }, [kind, effectiveNode, equipmentInstruments, nodeInstruments]);

  const activeDirectNodeInstruments = useMemo(() => {
    if (kind !== 'node' || !effectiveNode) return [];
    return directNodeInstruments.filter(i => i.context_id === effectiveNode.id);
  }, [kind, effectiveNode, directNodeInstruments]);

  const handleInstDragStart = (index: number) => setDraggedIndex(index);
  const handleInstDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    setDragOverIndex(index);
  };
  const handleInstDrop = (index: number) => {
    if (readOnly) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }
    if (draggedIndex === null || !onReorderInstruments) return;
    if (draggedIndex !== index) {
      const dragged = sortedNodeInstruments[draggedIndex];
      const directIds = new Set(activeDirectNodeInstruments.map((inst) => inst.id));
      if (dragged && directIds.has(dragged.id)) {
        const directItems = sortedNodeInstruments.filter((inst) => directIds.has(inst.id));
        const from = directItems.findIndex((inst) => inst.id === dragged.id);
        const to = sortedNodeInstruments.slice(0, index).filter((inst) => directIds.has(inst.id)).length;
        if (from !== -1) {
          const nextDirect = [...directItems];
          const [removed] = nextDirect.splice(from, 1);
          nextDirect.splice(Math.max(0, Math.min(nextDirect.length, to)), 0, removed);
          onReorderInstruments(nextDirect);
        }
      }
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };
  const handleInstDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const sortedNodeInstruments = useMemo(() => {
    return [...activeNodeInstruments].sort((a, b) => {
      const seqA = a.sequence_order ?? Infinity;
      const seqB = b.sequence_order ?? Infinity;
      return seqA - seqB;
    });
  }, [activeNodeInstruments]);

  const activeDirectNodeInstrumentIds = useMemo(
    () => new Set(activeDirectNodeInstruments.map((inst) => inst.id)),
    [activeDirectNodeInstruments],
  );

  const reviewStatusColor = (
    status?: string | null,
    kind: 'equipment' | 'node' | 'instrument' = 'instrument'
  ) => {
    if (status === 'final_and_reviewed') return 'var(--raven-success)';
    if (status === 'final' || status === 'in_progress') {
      if (kind === 'equipment') return 'var(--raven-info)';
      if (kind === 'node') return 'var(--raven-warning)';
      return 'var(--raven-primary-main)';
    }
    if (status === 'skipped') return 'var(--raven-primary-main)';
    return 'var(--raven-text-disabled)';
  };

  const renderReviewButton = (
    target: { kind: 'equipment' | 'equipmentInstrument' | 'node' | 'nodeInstrument'; id: string },
    status?: string | null,
    compact = false
  ) => {
    if (!onMarkReviewed) return null;
    const actionKey = `${target.kind}:${target.id}`;
    const isReviewed = status === 'final_and_reviewed';
    const isSaving = reviewingKey === actionKey;
    return (
      <button
        type="button"
        onClick={async () => {
          if (isReviewed || isSaving) return;
          try {
            setReviewingKey(actionKey);
            await onMarkReviewed(target);
          } finally {
            setReviewingKey((current) => (current === actionKey ? null : current));
          }
        }}
        disabled={isReviewed || isSaving}
        style={{
          padding: compact ? '3px 6px' : '6px 10px',
          fontSize: compact ? 10 : 12,
          fontWeight: 600,
          borderRadius: 4,
          border: '1px solid var(--raven-primary-light)',
          backgroundColor: isReviewed ? 'var(--raven-surface-tier3)' : 'var(--raven-surface-tier3)',
          color: 'var(--raven-primary-main)',
          cursor: isReviewed || isSaving ? 'not-allowed' : 'pointer',
          opacity: 1,
        }}
      >
        {isSaving ? 'Saving...' : isReviewed ? 'Reviewed' : 'Mark as Reviewed'}
      </button>
    );
  };

  const renderPfdAttachedInstruments = () => {
    if (kind === 'equipment') {
      if (!activeEquipment) return null;
      return (
        <div style={{ marginTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Instruments ({activeEquipmentInstruments.length})
            </div>
            {onMarkInstrument && (
              <button
                type="button"
                onClick={() => onMarkInstrument('equipment', activeEquipment.id)}
                disabled={readOnly}
                style={{
                  padding: '2px 8px',
                  fontSize: 9,
                  fontWeight: 500,
                  color: 'var(--raven-primary-main)',
                  backgroundColor: 'var(--raven-state-selected)',
                  border: '1px solid var(--raven-primary-light)',
                  borderRadius: 4,
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                  opacity: readOnly ? 0.55 : 1,
                }}
              >
                + Mark Instrument
              </button>
            )}
          </div>
          {activeEquipmentInstruments.length === 0 ? (
            <div style={{ padding: 12, textAlign: 'center', color: 'var(--raven-text-disabled)', fontSize: 12, backgroundColor: 'var(--raven-surface-tier2)', borderRadius: 6 }}>
              No instruments attached yet.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {activeEquipmentInstruments.map((inst) => (
                <div
                  key={inst.id}
                  onMouseEnter={() => onHighlightEntity?.(inst.id)}
                  onMouseLeave={() => onHighlightEntity?.(null)}
                  style={{ padding: '8px 10px', backgroundColor: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: 99, backgroundColor: reviewStatusColor(inst.status, 'instrument'), flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--raven-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getInstrumentPrimaryLabel(inst)}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)', marginTop: 1 }}>
                      {inst.instrument_type || 'Instrument'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                    <button type="button" onClick={() => onEditEquipmentInstrument?.(inst.id)} disabled={readOnly} style={{ padding: '3px 8px', fontSize: 10, fontWeight: 500, color: 'var(--raven-primary-main)', backgroundColor: 'var(--raven-state-selected)', border: '1px solid var(--raven-primary-light)', borderRadius: 3, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }}>
                      Edit
                    </button>
                    {renderReviewButton({ kind: 'equipmentInstrument', id: inst.id }, inst.status, true)}
                    <button type="button" onClick={() => onUnattachEquipmentInstrument?.(inst.id)} disabled={readOnly} style={{ padding: '3px 6px', fontSize: 10, color: 'var(--raven-text-disabled)', backgroundColor: 'transparent', border: '1px solid var(--raven-divider)', borderRadius: 3, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }} title="Unattach from equipment">
                      {'\u2715'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (!effectiveNode) return null;
    return (
      <div style={{ marginTop: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Instruments ({sortedNodeInstruments.length})
          </div>
          {onMarkInstrument && (
            <button
              type="button"
              onClick={() => onMarkInstrument('node', effectiveNode.id)}
              disabled={readOnly}
              style={{ padding: '2px 8px', fontSize: 9, fontWeight: 500, color: 'var(--raven-primary-main)', backgroundColor: 'var(--raven-state-selected)', border: '1px solid var(--raven-primary-light)', borderRadius: 4, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }}
            >
              + Mark Instrument
            </button>
          )}
        </div>
        {sortedNodeInstruments.length === 0 ? (
          <div style={{ padding: 12, textAlign: 'center', color: 'var(--raven-text-disabled)', fontSize: 12, backgroundColor: 'var(--raven-surface-tier2)', borderRadius: 6 }}>
            No instruments marked yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {sortedNodeInstruments.map((inst, index) => (
              <div
                key={inst.id}
                onMouseEnter={() => onHighlightEntity?.(inst.id)}
                onMouseLeave={() => onHighlightEntity?.(null)}
                style={{ padding: '8px 10px', backgroundColor: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
              >
                <span style={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--raven-surface-tier2)', color: 'var(--raven-text-secondary)', borderRadius: 3, fontSize: 9, fontWeight: 600 }}>
                  {inst.sequence_order ?? index + 1}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--raven-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {getInstrumentPrimaryLabel(inst)}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)', marginTop: 1 }}>
                    {inst.instrument_type || 'Instrument'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                  <button type="button" onClick={() => onEditInstrument?.(inst.id)} disabled={readOnly} style={{ padding: '3px 8px', fontSize: 10, fontWeight: 500, color: 'var(--raven-primary-main)', backgroundColor: 'var(--raven-state-selected)', border: '1px solid var(--raven-primary-light)', borderRadius: 3, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }}>
                    Edit
                  </button>
                  {renderReviewButton({ kind: 'nodeInstrument', id: inst.id }, inst.status, true)}
                  <button type="button" onClick={() => onUnattachInstrument?.(inst.id)} disabled={readOnly} style={{ padding: '3px 6px', fontSize: 10, color: 'var(--raven-text-disabled)', backgroundColor: 'transparent', border: '1px solid var(--raven-divider)', borderRadius: 3, cursor: readOnly ? 'not-allowed' : 'pointer', opacity: readOnly ? 0.55 : 1 }} title="Unattach from node">
                    {'\u2715'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Wire the stable PFD custom-renderer refs to the latest closures. Source/dest use
  // the same SourceDestSelector as P&ID; the dest mirror also stamps drain_type.
  // SourceDestSelector renders its own "Source"/"Destination" heading from `label`,
  // so no wrapper heading here (that would print the label twice).
  pfdSourceRefRenderRef.current = ({ value, readOnly: ro }) => (
    <SourceDestSelector
      label="Source"
      value={typeof value === 'string' ? value : ''}
      onChange={(val, refType, externalRef) => {
        setField('source_ref', val);
        if (refType === 'external' && externalRef?.pnid) {
          setField('source_pnid', externalRef.pnid);
        }
      }}
      aiData={aiData ?? null}
      nodes={nodes ?? []}
      equipments={equipments ?? []}
      instruments={instruments ?? []}
      includeDrain={false}
      currentNodeId={effectiveNode?.id}
      onHighlightEntity={onHighlightEntity}
      disabled={ro}
    />
  );
  pfdDestRefRenderRef.current = ({ value, readOnly: ro }) => (
    <SourceDestSelector
      label="Destination"
      value={typeof value === 'string' ? value : ''}
      onChange={(val, refType, externalRef) => {
        setField('dest_ref', val);
        setField('drain_type', refType === 'drain' ? val : null);
        if (refType === 'external' && externalRef?.pnid) {
          setField('dest_pnid', externalRef.pnid);
        }
      }}
      aiData={aiData ?? null}
      nodes={nodes ?? []}
      equipments={equipments ?? []}
      instruments={instruments ?? []}
      includeDrain
      currentNodeId={effectiveNode?.id}
      onHighlightEntity={onHighlightEntity}
      disabled={ro}
    />
  );
  pfdConnectedEqRenderRef.current = renderConnectedEquipmentSection;
  pfdLinkedPnidRenderRef.current = () => renderEquipmentDocumentLinks('pnid');
  pfdLinkedStreamRenderRef.current = renderPfdStreamDocumentLinks;

  const renderPfdFormFields = () => (
    <>
      {kind === 'equipment' && isMarkingExisting && values.equipment_no && (
        <div style={{
          padding: '8px 12px',
          background: 'var(--raven-surface-tier3)',
          border: '1px solid var(--raven-primary-light)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ color: 'var(--raven-primary-main)', fontWeight: 600 }}>{values.equipment_no}</span>
          <span style={{ color: 'var(--raven-success)', fontSize: 11 }}>{values.equipment_type || 'Equipment'}</span>
        </div>
      )}
      {kind === 'node' && (
        <div style={{
          fontSize: 12,
          padding: '8px 12px',
          background: 'var(--raven-surface-tier3)',
          border: '1px solid var(--raven-primary-light)',
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 8
        }}>
          <span style={{ color: 'var(--raven-primary-main)', fontWeight: 600 }}>Stream {values.line_no || values.node_no || '?'}</span>
        </div>
      )}
      {isMarkingExisting && kind === 'equipment' && activeEquipment && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {renderReviewButton({ kind: 'equipment', id: activeEquipment.id }, activeEquipment.status)}
        </div>
      )}
      {isMarkingExisting && kind === 'node' && effectiveNode && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {renderReviewButton({ kind: 'node', id: effectiveNode.id }, effectiveNode.status)}
        </div>
      )}
      {pfdSchema ? (
        <SchemaFieldRenderer
          sections={pfdSchema.sections}
          values={values}
          setField={setField}
          customRenderers={kind === 'equipment' ? pfdEquipmentCustomRenderers : pfdNodeCustomRenderers}
          readOnly={readOnly}
        />
      ) : (
        <div style={{ padding: 10, color: 'var(--raven-text-secondary)', fontSize: 12 }}>Loading PFD form...</div>
      )}
      {renderPfdAttachedInstruments()}
    </>
  );


  // Always render inline for wizard mode (no modal overlay)
  return (
    <div data-zone="form">
      {/* The SlidePanel that wraps this form already renders a header + close
          control, so the form's own title row carries no second ✕. */}
      <h3 style={{ margin: 0, fontSize: 16, color: 'var(--raven-text-primary)' }}>{title}</h3>

      {isMarkingExisting && (
        <div style={{ 
          marginTop: 8, 
          padding: '6px 10px', 
          background: 'var(--raven-surface-tier2)', 
          border: '1px solid var(--raven-divider)', 
          borderRadius: 6, 
          fontSize: 11, 
          color: 'var(--raven-text-primary)' 
        }}>
          Marking existing item from AI data
        </div>
      )}

      {regionInfoLines.length > 0 && kind !== 'equipment' && (
        <div style={{ marginTop: 10, padding: 10, background: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, color: 'var(--raven-text-primary)' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {regionInfoLines.length === 1 ? 'Region' : 'Regions'}
          </div>
          {regionInfoLines.map((line) => (
            <div key={line}>{line}</div>
          ))}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        onKeyDownCapture={(e) => {
          if (!nodeQuickEnabled) return;
          const target = e.target as HTMLElement | null;
          const isTextarea = target?.tagName === 'TEXTAREA';

          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
            return;
          }

          if (!isTextarea && e.key === 'Enter' && e.shiftKey) {
            e.preventDefault();
            nodeGoPrev();
          }
        }}
        style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {isPfdMode && renderPfdFormFields()}

        {!isPfdMode && kind === 'equipment' && (
          <>
            {/* Equipment identity header (like node's green Node # box) */}
            {isMarkingExisting && values.equipment_no && (
              <div style={{
                padding: '8px 12px',
                background: 'var(--raven-surface-tier3)',
                border: '1px solid var(--raven-primary-light)',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{ color: 'var(--raven-primary-main)', fontWeight: 600 }}>{values.equipment_no}</span>
                <span style={{ color: 'var(--raven-success)', fontSize: 11 }}>
                  {values.equipment_type || 'Equipment'}
                </span>
              </div>
            )}
            {isMarkingExisting && activeEquipment && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {renderReviewButton(
                  { kind: 'equipment', id: activeEquipment.id },
                  activeEquipment.status
                )}
              </div>
            )}

            <label style={{ fontSize: 12 }}>
              Equipment No (required)
              {/* The tag stays editable on every drawing: a mistagged element is
                  re-pointed at the right asset by renaming it, primary or not. */}
              <TagAutocompleteInput
                value={values.equipment_no ?? ''}
                onChange={(next) => setField('equipment_no', next)}
                onSettle={handleEquipmentTagSettled}
                docId={currentDocument?.id}
                kind="equipment"
                disabled={readOnly}
                required
                style={{ marginTop: 4, fontWeight: 500, background: readOnly ? 'var(--raven-surface-tier2)' : 'white' }}
              />
            </label>

            <div>
              {/* The label points at the select rather than wrapping it, so the
                  picker's own status line stays out of the field's name. */}
              <label htmlFor={OWNER_DOMAIN_SELECT_ID} style={{ display: 'block', fontSize: 12 }}>
                Domain
              </label>
              <div style={{ fontSize: 11, color: 'var(--raven-text-secondary)', marginTop: 2 }}>
                The plant that owns this asset.
              </div>
              <DomainSelect
                id={OWNER_DOMAIN_SELECT_ID}
                storedValue={storedOwnerDomainId}
                onChange={(next) => setField('owner_domain_id', next)}
                drawingDomainId={drawingDomainId}
                drawingPlantName={currentDocument?.plant ?? null}
                disabled={readOnly}
                style={{
                  width: '100%',
                  padding: 8,
                  marginTop: 4,
                  border: '1px solid var(--raven-divider)',
                  borderRadius: 6,
                  background: readOnly ? 'var(--raven-surface-tier2)' : 'var(--raven-surface-tier1)',
                }}
              />
            </div>

            {/* The mark governs the fields below it, so it is answered first. */}
            {renderPrimarySourceBlock()}

            {renderCompoundSplitSection()}

            {/* Equipment Type + Description in a grouped section */}
            <div style={{ padding: 10, background: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 8 }}>Details</div>
              <label style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Equipment Type (required)
                {equipmentTypes.error ? (
                  /* The taxonomy is unreachable. The API skips its check in
                     exactly this case, so keep the field usable rather than
                     trapping the reviewer behind a disabled control. */
                  <input
                    value={canonicalFieldsLocked
                      ? lockedEquipmentType
                      : (values.equipment_type ?? '')}
                    onChange={(e) => changeEquipmentType(e.target.value)}
                    disabled={assetFieldsLocked}
                    style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, background: assetFieldsLocked ? 'var(--raven-surface-tier2)' : 'white' }}
                    required
                  />
                ) : (
                  <select
                    /* A stored value outside the reviewed list shows as no
                       selection, so `required` makes the reviewer classify it
                       before the mark can be saved. */
                    value={canonicalFieldsLocked
                      ? lockedEquipmentType
                      : (isUnreviewedEquipmentType ? '' : (values.equipment_type ?? ''))}
                    onChange={(e) => changeEquipmentType(e.target.value)}
                    disabled={equipmentTypes.loading || assetFieldsLocked}
                    style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, background: assetFieldsLocked ? 'var(--raven-surface-tier2)' : 'var(--raven-surface-tier1)' }}
                    required
                  >
                    <option value="" disabled>
                      {equipmentTypes.loading ? 'Loading…' : 'Select an equipment type'}
                    </option>
                    {/* The register's type need not be in the reviewed list, and a
                        locked field must still show what the register holds. */}
                    {lockedTypeIsOffList && (
                      <option value={lockedEquipmentType}>{lockedEquipmentType}</option>
                    )}
                    {equipmentTypes.types.map((item) => (
                      <option key={item.key} value={item.key}>{item.label}</option>
                    ))}
                  </select>
                )}
                {isUnreviewedEquipmentType && !equipmentTypes.error && !assetFieldsLocked && (
                  <div style={{ fontSize: 11, color: 'var(--raven-warning-text)', marginTop: 4 }}>
                    Recorded as “{values.equipment_type}”, which is not in the reviewed
                    list. Choose the reviewed type to save this equipment.
                  </div>
                )}
                {equipmentTypes.error && (
                  <div style={{ fontSize: 11, color: 'var(--raven-error-text)', marginTop: 4 }}>
                    {equipmentTypes.error}. Type the equipment type instead.
                  </div>
                )}
              </label>
              {equipmentSubtypeOptions.length > 0 && (
                <label style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                  Sub-type
                  <select
                    value={values.subtype ?? ''}
                    onChange={(e) => setField('subtype', e.target.value || null)}
                    disabled={assetFieldsLocked}
                    style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, background: assetFieldsLocked ? 'var(--raven-surface-tier2)' : 'var(--raven-surface-tier1)' }}
                  >
                    <option value="">-- None --</option>
                    {/* A stored label the vocabulary no longer lists still has to read back. */}
                    {values.subtype && !equipmentSubtypeOptions.includes(values.subtype) && (
                      <option value={values.subtype}>{values.subtype}</option>
                    )}
                    {equipmentSubtypeOptions.map((subtype) => (
                      <option key={subtype} value={subtype}>{subtype}</option>
                    ))}
                  </select>
                </label>
              )}
              <label style={{ fontSize: 12, display: 'block' }}>
                Description (required)
                <input
                  value={canonicalFieldsLocked
                    ? equipmentRegisterEntry?.display_name ?? ''
                    : (values.description ?? '')}
                  onChange={(e) => setField('description', e.target.value)}
                  disabled={assetFieldsLocked}
                  style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, background: assetFieldsLocked ? 'var(--raven-surface-tier2)' : 'white' }}
                  required
                />
              </label>
              {assetFieldsLocked && !canonicalFieldsLocked && (
                <div style={{ fontSize: 11, color: 'var(--raven-warning-text)', marginTop: 6 }}>
                  Tick Primary P&amp;ID to edit the type, sub-type and description.
                </div>
              )}
            </div>

            {renderDetailsFields()}

            <label style={{ fontSize: 12 }}>
              Painting specification (optional)
              <input
                value={values.painting_spec ?? ''}
                onChange={(e) => setField('painting_spec', e.target.value)}
                placeholder="Painting or coating specification..."
                disabled={readOnly}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12 }}
              />
            </label>

            {/* Connected Equipment (bidirectional peer links) */}
            {renderConnectedEquipmentSection()}

            {/* A drawing with no domain belongs to no plant, so it has no sets to show. */}
            {currentDocument?.id && currentDocument.domain_id && (
              <RedundancySetSection
                docId={currentDocument.id}
                domainId={currentDocument.domain_id}
                savedTag={activeEquipment?.equipment_no ?? ''}
                kind="equipment"
                readOnly={readOnly}
              />
            )}

            {/* Linked PFDs (bidirectional cross-document links) */}
            {renderEquipmentDocumentLinks('pfd')}

            {/* Notes field */}
            <label style={{ fontSize: 12 }}>
              Notes (optional) — attached to equipment {values.equipment_no || 'this equipment'}
              <textarea
                value={values.notes ?? ''}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Additional notes or metadata..."
                rows={2}
                disabled={readOnly}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, resize: 'vertical', fontSize: 12 }}
              />
            </label>

            {/* Attached Instruments */}
            {isMarkingExisting && (
              <div style={{ marginTop: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Instruments ({activeEquipmentInstruments.length})
                  </div>
                  {onMarkInstrument && activeEquipment && (
                    <button
                      type="button"
                      onClick={() => onMarkInstrument('equipment', activeEquipment.id)}
                      disabled={readOnly}
                      style={{
                        padding: '2px 8px',
                        fontSize: 9,
                        fontWeight: 500,
                        color: 'var(--raven-primary-main)',
                        backgroundColor: 'var(--raven-state-selected)',
                        border: '1px solid var(--raven-primary-light)',
                        borderRadius: 4,
                        cursor: readOnly ? 'not-allowed' : 'pointer',
                        opacity: readOnly ? 0.55 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                      }}
                      title="Draw an instrument bounding box on the canvas"
                    >
                      + Mark Instrument
                    </button>
                  )}
                </div>

                {activeEquipmentInstruments.length === 0 ? (
                  <div style={{
                    padding: 12,
                    textAlign: 'center',
                    color: 'var(--raven-text-disabled)',
                    fontSize: 12,
                    backgroundColor: 'var(--raven-surface-tier2)',
                    borderRadius: 6,
                  }}>
                    No instruments attached yet.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {activeEquipmentInstruments.map((inst) => {
                      const isUnattachConfirm = confirmUnattachEquip === inst.id;
                      const hasBbox = Boolean(inst.bbox);

                      return (
                        <div
                          key={inst.id}
                          onMouseEnter={() => onHighlightEntity?.(inst.id)}
                          onMouseLeave={() => onHighlightEntity?.(null)}
                          style={{
                            padding: '8px 10px',
                            backgroundColor: 'var(--raven-surface-tier2)',
                            border: '1px solid var(--raven-divider)',
                            borderRadius: 6,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                          }}
                        >
                          {/* Status indicator */}
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: 99,
                              backgroundColor: reviewStatusColor(inst.status, 'instrument'),
                              flexShrink: 0,
                            }}
                            title={inst.status === 'final_and_reviewed' ? 'Reviewed' : hasBbox ? 'Marked' : 'Not marked'}
                          />

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--raven-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {getInstrumentPrimaryLabel(inst)}
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)', marginTop: 1 }}>
                                {getEquipmentInstrumentName(inst.instrument_type)}
                              {inst.subtype ? ` \u00b7 ${inst.subtype}` : ''}
                            </div>
                          </div>

                          {/* Actions: Edit + Unattach */}
                          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                            {isUnattachConfirm ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => {
                                    onUnattachEquipmentInstrument?.(inst.id);
                                    setConfirmUnattachEquip(null);
                                  }}
                                  disabled={readOnly}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: 10,
                                    fontWeight: 500,
                                    color: 'white',
                                    backgroundColor: 'var(--raven-error)',
                                    border: 'none',
                                    borderRadius: 3,
                                    cursor: readOnly ? 'not-allowed' : 'pointer',
                                    opacity: readOnly ? 0.55 : 1,
                                  }}
                                >
                                  Confirm
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmUnattachEquip(null)}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: 10,
                                    fontWeight: 500,
                                    color: 'var(--raven-text-secondary)',
                                    backgroundColor: 'white',
                                    border: '1px solid var(--raven-divider)',
                                    borderRadius: 3,
                                    cursor: 'pointer',
                                  }}
                                >
                                  {'\u2715'}
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => onEditEquipmentInstrument?.(inst.id)}
                                  disabled={readOnly}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: 10,
                                    fontWeight: 500,
                                    color: 'var(--raven-primary-main)',
                                    backgroundColor: 'var(--raven-state-selected)',
                                    border: '1px solid var(--raven-primary-light)',
                                    borderRadius: 3,
                                    cursor: readOnly ? 'not-allowed' : 'pointer',
                                    opacity: readOnly ? 0.55 : 1,
                                  }}
                                >
                                  Edit
                                </button>
                                {renderReviewButton(
                                  { kind: 'equipmentInstrument', id: inst.id },
                                  inst.status,
                                  true
                                )}
                                <button
                                  type="button"
                                  onClick={() => setConfirmUnattachEquip(inst.id)}
                                  disabled={readOnly}
                                  style={{
                                    padding: '3px 6px',
                                    fontSize: 10,
                                    color: 'var(--raven-text-disabled)',
                                    backgroundColor: 'transparent',
                                    border: '1px solid var(--raven-divider)',
                                    borderRadius: 3,
                                    cursor: readOnly ? 'not-allowed' : 'pointer',
                                    opacity: readOnly ? 0.55 : 1,
                                  }}
                                  title="Unattach from equipment"
                                >
                                  {'\u2715'}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {!isPfdMode && kind === 'node' && (
          <>
            <label style={{ fontSize: 12 }}>
              Material-flow marking
              <select
                value={values.is_duct ? 'duct' : 'pipeline'}
                onChange={(e) => setField('is_duct', e.target.value === 'duct')}
                disabled={readOnly}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
              >
                <option value="pipeline">Pipeline (linear path)</option>
                <option value="duct">Duct (rectangular box)</option>
              </select>
            </label>
            <div style={{
              fontSize: 12,
              padding: '8px 12px',
              background: 'var(--raven-surface-tier3)',
              border: '1px solid var(--raven-primary-light)',
              borderRadius: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <span style={{ color: 'var(--raven-primary-main)', fontWeight: 600 }}>Node #{values.node_no ?? '?'}</span>
              <span style={{ color: 'var(--raven-success)', fontSize: 11 }}>(auto-assigned)</span>
            </div>
            {isMarkingExisting && effectiveNode && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                {renderReviewButton(
                  { kind: 'node', id: effectiveNode.id },
                  effectiveNode.status
                )}
              </div>
            )}

            {renderLineSpecFields()}

            <label style={{ fontSize: 12 }}>
              Line No/Description
              <input
                ref={nodeLineNoRef}
                value={values.line_no ?? ''}
                onChange={(e) => setField('line_no', e.target.value)}
                onKeyDown={(e) => {
                  if (!nodeQuickEnabled) return;
                  if (e.key !== 'Enter' && e.key !== 'Tab') return;
                  if (e.key === 'Tab' && e.shiftKey) return;
                  e.preventDefault();
                  nodeGoNext();
                }}
                style={{
                  width: '100%',
                  padding: 8,
                  marginTop: 4,
                  border: nodeQuickEnabled && nodeQuickStep === 'line_no' ? '2px solid var(--raven-primary-main)' : '1px solid var(--raven-divider)',
                  borderRadius: 6,
                  fontWeight: 500,
                }}
                required={((values.line_type as LineType | undefined) ?? 'process') !== 'instrument'}
              />
            </label>

            {/* Line Type / Instrument Line Subtype */}
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ fontSize: 12, flex: 1 }}>
                Line Type
                <select
                  value={(values.line_type as LineType | undefined) ?? 'process'}
                  onChange={(e) => {
                    const next = e.target.value as LineType;
                    setField('line_type', next);
                    if (next !== 'instrument' && values.instrument_line_subtype) {
                      setField('instrument_line_subtype', null);
                    }
                  }}
                  style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                >
                  <option value="process">Process Line</option>
                  <option value="instrument">Instrument Line</option>
                </select>
              </label>
              {((values.line_type as LineType | undefined) ?? 'process') === 'instrument' && (
                <label style={{ fontSize: 12, flex: 1 }}>
                  Subtype
                  <select
                    value={(values.instrument_line_subtype as InstrumentLineSubtype | null | undefined) ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setField('instrument_line_subtype', v ? (v as InstrumentLineSubtype) : null);
                    }}
                    style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                  >
                    <option value="">Select subtype…</option>
                    {(Object.keys(INSTRUMENT_LINE_SUBTYPE_LABELS) as InstrumentLineSubtype[]).map((key) => (
                      <option key={key} value={key}>{INSTRUMENT_LINE_SUBTYPE_LABELS[key]}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            {/* Source Section */}
            <div style={{ padding: 10, background: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 8 }}>Source</div>
              <div style={nodeQuickEnabled && nodeQuickStep === 'source_ref' ? { outline: '2px solid var(--raven-primary-main)', borderRadius: 8, padding: 6 } : undefined}>
                <SourceDestSelector
                  label="Source (required)"
                  value={values.source_ref ?? ''}
                  onChange={(val, refType, externalRef) => {
                    setField('source_ref', val);
                    if (refType === 'external' && externalRef?.pnid) {
                      setField('source_pnid', externalRef.pnid);
                    }
                  }}
                  aiData={aiData}
                  nodes={nodes}
                  equipments={equipments}
                  instruments={instruments}
                  includeDrain={false}
                  currentNodeId={values._markExistingId}
                  forceExpanded={nodeQuickEnabled && nodeQuickStep === 'source_ref'}
                  autoFocusSearch={nodeQuickEnabled && nodeQuickStep === 'source_ref'}
                  onAdvance={nodeQuickEnabled ? nodeGoNext : undefined}
                  onHighlightEntity={onHighlightEntity}
                />
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--raven-text-secondary)', marginBottom: 4 }}>Source PnID</div>
                <div style={nodeQuickEnabled && nodeQuickStep === 'source_pnid' ? { outline: '2px solid var(--raven-primary-main)', borderRadius: 8, padding: 6 } : undefined}>
                  <AutocompleteDropdown
                    inputRef={nodeSourcePnidRef}
                    onCommit={nodeQuickEnabled ? nodeGoNext : undefined}
                    value={values.source_pnid ?? ''}
                    onChange={(val) => setField('source_pnid', val || null)}
                    placeholder="Select or type P&ID..."
                    options={[
                      ...(pnidNoDefault ? [{ value: pnidNoDefault, label: `${pnidNoDefault} (Current)` }] : []),
                      ...(aiData?.tie_ins?.map(tieIn => ({
                        value: tieIn.tie_in_id,
                        label: `${tieIn.direction}: ${tieIn.service || 'Tie-in'}`
                      })) || [])
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Destination Section */}
            <div style={{ padding: 10, background: 'var(--raven-surface-tier2)', border: '1px solid var(--raven-divider)', borderRadius: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)', marginBottom: 8 }}>Destination</div>
              <div style={nodeQuickEnabled && nodeQuickStep === 'dest_ref' ? { outline: '2px solid var(--raven-primary-main)', borderRadius: 8, padding: 6 } : undefined}>
                <SourceDestSelector
                  label="Destination (required)"
                  onHighlightEntity={onHighlightEntity}
                  value={values.dest_ref ?? ''}
                  onChange={(val, refType, externalRef) => {
                    setField('dest_ref', val);
                    if (refType === 'drain' && val) {
                      setField('drain_type', val);
                    } else if (refType !== 'drain') {
                      if (values.drain_type && !isDrainDestination(val)) {
                        setField('drain_type', null);
                      }
                    }
                    if (refType === 'external' && externalRef?.pnid) {
                      setField('dest_pnid', externalRef.pnid);
                    }
                  }}
                  aiData={aiData}
                  nodes={nodes}
                  equipments={equipments}
                  instruments={instruments}
                  includeDrain={true}
                  currentNodeId={values._markExistingId}
                  forceExpanded={nodeQuickEnabled && nodeQuickStep === 'dest_ref'}
                  autoFocusSearch={nodeQuickEnabled && nodeQuickStep === 'dest_ref'}
                  onAdvance={nodeQuickEnabled ? nodeGoNext : undefined}
                />
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--raven-text-secondary)', marginBottom: 4 }}>Destination PnID</div>
                <div style={nodeQuickEnabled && nodeQuickStep === 'dest_pnid' ? { outline: '2px solid var(--raven-primary-main)', borderRadius: 8, padding: 6 } : undefined}>
                  <AutocompleteDropdown
                    inputRef={nodeDestPnidRef}
                    onCommit={nodeQuickEnabled ? nodeGoNext : undefined}
                    value={values.dest_pnid ?? ''}
                    onChange={(val) => setField('dest_pnid', val || null)}
                    placeholder="Select or type P&ID..."
                    options={[
                      ...(pnidNoDefault ? [{ value: pnidNoDefault, label: `${pnidNoDefault} (Current)` }] : []),
                      ...(aiData?.tie_ins?.map(tieIn => ({
                        value: tieIn.tie_in_id,
                        label: `${tieIn.direction}: ${tieIn.service || 'Tie-in'}`
                      })) || [])
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Drain Type display */}
            {values.drain_type && (
              <div style={{
                padding: 10,
                background: 'var(--raven-warning-bg)',
                border: '1px solid var(--raven-warning-border)',
                borderRadius: 6
              }}>
                <div style={{ fontSize: 12 }}>
                  <span style={{ fontWeight: 600, color: 'var(--raven-warning-text)' }}>Drain Type: </span>
                  <span>{values.drain_type} — {DRAIN_TYPES[values.drain_type as keyof typeof DRAIN_TYPES] || values.drain_type}</span>
                </div>
              </div>
            )}

            {/* === Line Properties (collapsible) === */}
            <div style={{ border: '1px solid var(--raven-divider)', borderRadius: 6 }}>
              <button
                type="button"
                onClick={() => setLinePropsExpanded((v) => !v)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 10px',
                  background: 'var(--raven-surface-tier2)',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--raven-text-primary)',
                }}
              >
                <span>Line Properties</span>
                <span style={{ color: 'var(--raven-text-secondary)' }}>{linePropsExpanded ? '▾' : '▸'}</span>
              </button>
              {linePropsExpanded && (
                <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--raven-divider)' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                    <label style={{ fontSize: 12, flex: 1 }}>
                      Installation Type
                      <select
                        value={(values.installation_type as InstallationType | null | undefined) ?? ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setField('installation_type', v ? (v as InstallationType) : null);
                        }}
                        style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                      >
                        <option value="">None</option>
                        {(Object.keys(INSTALLATION_TYPE_LABELS) as InstallationType[]).map((key) => (
                          <option key={key} value={key}>{INSTALLATION_TYPE_LABELS[key]}</option>
                        ))}
                      </select>
                    </label>
                    <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8, whiteSpace: 'nowrap' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(values.is_jacketed)}
                        onChange={(e) => setField('is_jacketed', e.target.checked)}
                      />
                      Jacketed
                    </label>
                  </div>

                  <label style={{ fontSize: 12 }}>
                    Tracing
                    <select
                      value={(values.tracing_type as TracingType | null | undefined) ?? ''}
                      onChange={(e) => {
                        const v = e.target.value;
                        setField('tracing_type', v ? (v as TracingType) : null);
                      }}
                      style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                    >
                      <option value="">None</option>
                      {(Object.keys(TRACING_TYPE_LABELS) as TracingType[]).map((key) => (
                        <option key={key} value={key}>{key === 'none' ? 'No Tracing' : TRACING_TYPE_LABELS[key]}</option>
                      ))}
                    </select>
                  </label>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <label style={{ fontSize: 12, flex: 1 }}>
                      Flow Profile
                      <select
                        value={(values.flow_profile as FlowProfile | null | undefined) ?? ''}
                        onChange={(e) => {
                          const next = e.target.value ? (e.target.value as FlowProfile) : null;
                          setField('flow_profile', next);
                          if (next !== 'sloped' && values.slope_ratio) {
                            setField('slope_ratio', null);
                          }
                        }}
                        style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                      >
                        <option value="">None</option>
                        {(Object.keys(FLOW_PROFILE_LABELS) as FlowProfile[]).map((key) => (
                          <option key={key} value={key}>{FLOW_PROFILE_LABELS[key]}</option>
                        ))}
                      </select>
                    </label>
                    {values.flow_profile === 'sloped' && (
                      <label style={{ fontSize: 12, flex: 1 }}>
                        Slope (e.g. 1/100)
                        <input
                          type="text"
                          value={values.slope_ratio ?? ''}
                          onChange={(e) => setField('slope_ratio', e.target.value || null)}
                          placeholder="1/100"
                          style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12 }}
                        />
                      </label>
                    )}
                  </div>

                  <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(values.is_two_phase)}
                      onChange={(e) => setField('is_two_phase', e.target.checked)}
                    />
                    Two-phase flow
                  </label>
                  {values.is_two_phase && (
                    <div style={{
                      padding: '6px 10px',
                      background: 'var(--raven-warning-bg)',
                      border: '1px solid var(--raven-warning-border)',
                      borderRadius: 6,
                      fontSize: 11,
                      color: 'var(--raven-warning-text)',
                    }}>
                      ⚠ Two-phase flow — vibration / support analysis required
                    </div>
                  )}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <label style={{ fontSize: 12, flex: 1 }}>
                      Flow Direction
                      <select
                        value={(values.flow_direction as FlowDirection | null | undefined) ?? ''}
                        onChange={(e) => {
                          const next = e.target.value ? (e.target.value as FlowDirection) : null;
                          setField('flow_direction', next);
                          if (next !== 'bidirectional_primary' && values.primary_direction) {
                            setField('primary_direction', null);
                          }
                        }}
                        style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                      >
                        <option value="">None</option>
                        {(Object.keys(FLOW_DIRECTION_LABELS) as FlowDirection[]).map((key) => (
                          <option key={key} value={key}>{FLOW_DIRECTION_LABELS[key]}</option>
                        ))}
                      </select>
                    </label>
                    {values.flow_direction === 'bidirectional_primary' && (
                      <label style={{ fontSize: 12, flex: 1 }}>
                        Primary Direction
                        <select
                          value={(values.primary_direction as PrimaryDirection | null | undefined) ?? ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            setField('primary_direction', v ? (v as PrimaryDirection) : null);
                          }}
                          style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12, background: 'white' }}
                        >
                          <option value="">None</option>
                          {(Object.keys(PRIMARY_DIRECTION_LABELS) as PrimaryDirection[]).map((key) => (
                            <option key={key} value={key}>{PRIMARY_DIRECTION_LABELS[key]}</option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>

            <label style={{ fontSize: 12 }}>
              Painting specification (optional)
              <input
                value={values.painting_spec ?? ''}
                onChange={(e) => setField('painting_spec', e.target.value)}
                placeholder="Painting or coating specification..."
                disabled={readOnly}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, fontSize: 12 }}
              />
            </label>

            <label style={{ fontSize: 12 }}>
              Drawing note (from source)
              <textarea
                value={values.note ?? ''}
                onChange={(e) => setField('note', e.target.value)}
                placeholder="Note from the source drawing (Excel: NOTE)..."
                rows={2}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, resize: 'vertical', fontSize: 12 }}
              />
            </label>

            <label style={{ fontSize: 12 }}>
              Notes (optional) — attached to line {values.line_no || 'this line'}
              <textarea
                value={values.notes ?? ''}
                onChange={(e) => setField('notes', e.target.value)}
                placeholder="Additional notes or metadata..."
                rows={2}
                disabled={readOnly}
                style={{ width: '100%', padding: 8, marginTop: 4, border: '1px solid var(--raven-divider)', borderRadius: 6, resize: 'vertical', fontSize: 12 }}
              />
            </label>

            {/* === Instrument Sequence Management === */}
            {effectiveNode && values._markExistingId && (
              <>
                <div style={{ borderTop: '1px solid var(--raven-divider)', marginTop: 4, paddingTop: 10 }}>
                  {/* Branch Candidates */}
                  {branchCandidates.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--raven-primary-main)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        Branch Candidates ({branchCandidates.length})
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
                        {branchCandidates.map((candidate) => {
                          const label = candidate.branchLabel || candidate.branchLineNo || `Node #${candidate.branchRef ?? '?'}`;
                          const angle = candidate.branchAngleDeg != null ? `${candidate.branchAngleDeg.toFixed(1)}°` : '—';
                          const dist = candidate.branchDistancePx != null ? `${Math.round(candidate.branchDistancePx)}px` : '—';
                          return (
                            <div
                              key={candidate.id}
                              style={{
                                padding: '6px 8px',
                                borderRadius: 6,
                                border: '1px solid var(--raven-primary-light)',
                                backgroundColor: 'var(--raven-state-hover)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 8,
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--raven-text-secondary)' }}>
                                  Branch to {label}
                                </div>
                                <div style={{ fontSize: 9, color: 'var(--raven-text-secondary)' }}>
                                  Angle {angle} · Dist {dist}
                                </div>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <button
                                  type="button"
                                  onClick={() => onConfirmBranchCandidate?.(candidate.id)}
                                  disabled={readOnly}
                                  style={{
                                    padding: '3px 8px',
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: 'white',
                                    backgroundColor: 'var(--raven-primary-main)',
                                    border: 'none',
                                    borderRadius: 4,
                                    cursor: readOnly ? 'not-allowed' : 'pointer',
                                    opacity: readOnly ? 0.55 : 1,
                                  }}
                                >
                                  Add
                                </button>
                                <button
                                  type="button"
                                  onClick={() => onIgnoreBranchCandidate?.(candidate.id)}
                                  disabled={readOnly}
                                  style={{
                                    padding: '3px 6px',
                                    fontSize: 10,
                                    color: 'var(--raven-text-secondary)',
                                    backgroundColor: 'transparent',
                                    border: '1px solid rgba(0,0,0,0.08)',
                                    borderRadius: 4,
                                    cursor: readOnly ? 'not-allowed' : 'pointer',
                                    opacity: readOnly ? 0.55 : 1,
                                  }}
                                >
                                  Ignore
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Instruments Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Instruments ({sortedNodeInstruments.length})
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {onMarkInstrument && effectiveNode && (
                        <button
                          type="button"
                          onClick={() => onMarkInstrument('node', effectiveNode.id)}
                          disabled={readOnly}
                          style={{
                            padding: '2px 8px',
                            fontSize: 9,
                            fontWeight: 500,
                            color: 'var(--raven-primary-main)',
                            backgroundColor: 'var(--raven-state-selected)',
                            border: '1px solid var(--raven-primary-light)',
                            borderRadius: 4,
                            cursor: readOnly ? 'not-allowed' : 'pointer',
                            opacity: readOnly ? 0.55 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                          title="Draw an instrument bounding box on the canvas"
                        >
                          + Mark Instrument
                        </button>
                      )}
                      {onAutoAddBranches && effectiveNode?.polyline && (
                        <button
                          type="button"
                          onClick={async () => {
                            setIsDetectingBranches(true);
                            try {
                              await onAutoAddBranches(effectiveNode.id);
                            } finally {
                              setIsDetectingBranches(false);
                            }
                          }}
                          disabled={readOnly || isDetectingBranches}
                          style={{
                            padding: '2px 8px',
                            fontSize: 9,
                            fontWeight: 500,
                            color: isDetectingBranches ? 'var(--raven-text-disabled)' : 'var(--raven-primary-main)',
                            backgroundColor: isDetectingBranches ? 'var(--raven-surface-tier3)' : 'var(--raven-state-selected)',
                            border: `1px solid ${isDetectingBranches ? 'var(--raven-divider)' : 'var(--raven-primary-light)'}`,
                            borderRadius: 4,
                            cursor: readOnly || isDetectingBranches ? 'not-allowed' : 'pointer',
                            opacity: readOnly ? 0.55 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                          title="Auto-detect intersections with other marked lines"
                        >
                          {isDetectingBranches ? '...' : '\u2935'} Branches
                        </button>
                      )}
                      {onAutoSequence && effectiveNode?.polyline && activeDirectNodeInstruments.some(i => i.bbox) && (
                        <button
                          type="button"
                          onClick={async () => {
                            setIsAutoSequencing(true);
                            try {
                              await onAutoSequence(effectiveNode.id);
                            } finally {
                              setIsAutoSequencing(false);
                            }
                          }}
                          disabled={readOnly || isAutoSequencing}
                          style={{
                            padding: '2px 8px',
                            fontSize: 9,
                            fontWeight: 500,
                            color: isAutoSequencing ? 'var(--raven-text-disabled)' : 'var(--raven-success)',
                            backgroundColor: isAutoSequencing ? 'var(--raven-surface-tier3)' : 'var(--raven-state-selected)',
                            border: `1px solid ${isAutoSequencing ? 'var(--raven-divider)' : 'var(--raven-primary-light)'}`,
                            borderRadius: 4,
                            cursor: readOnly || isAutoSequencing ? 'not-allowed' : 'pointer',
                            opacity: readOnly ? 0.55 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                          }}
                          title="Auto-sequence instruments by position along the line"
                        >
                          {isAutoSequencing ? '...' : '\u2195'} Auto-sequence
                        </button>
                      )}
                      {activeDirectNodeInstruments.length > 1 && onReorderInstruments && (
                        <span style={{ fontSize: 9, color: 'var(--raven-text-disabled)' }}>Drag to reorder</span>
                      )}
                    </div>
                  </div>

                  {/* Instrument List */}
                  {sortedNodeInstruments.length === 0 ? (
                    <div style={{
                      padding: '12px 10px',
                      textAlign: 'center',
                      color: 'var(--raven-text-disabled)',
                      fontSize: 12,
                      backgroundColor: 'var(--raven-surface-tier2)',
                      borderRadius: 6,
                    }}>
                      No instruments marked yet.
                      <br />
                      <span style={{ fontSize: 10, color: 'var(--raven-text-secondary)' }}>Draw on canvas to add.</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {sortedNodeInstruments.map((inst, index) => {
                        const isUnattachConfirm = confirmUnattach === inst.id;
                        const typeName = inst.instrument_type ? getInstrumentCategoryName(inst.instrument_type) : 'Unknown';
                        const isBranch = Boolean(inst.branch_ref || inst.branch_direction);
                        const displaySequence = inst.sequence_order ?? index + 1;
                        const isDraggingItem = draggedIndex === index;
                        const isDragOver = dragOverIndex === index;
                        const hasBbox = Boolean(inst.bbox);
                        const canReorderInstrument = Boolean(onReorderInstruments)
                          && activeDirectNodeInstruments.length > 1
                          && activeDirectNodeInstrumentIds.has(inst.id);

                        return (
                          <div
                            key={inst.id}
                            draggable={canReorderInstrument}
                            onDragStart={canReorderInstrument ? () => handleInstDragStart(index) : undefined}
                            onDragOver={(e) => handleInstDragOver(e, index)}
                            onDrop={() => handleInstDrop(index)}
                            onDragEnd={handleInstDragEnd}
                            onMouseEnter={() => onHighlightEntity?.(inst.id)}
                            onMouseLeave={() => onHighlightEntity?.(null)}
                            style={{
                              padding: '8px 10px',
                              backgroundColor: isBranch ? 'var(--raven-warning-bg)' : 'var(--raven-surface-tier2)',
                              border: `1px solid ${isBranch ? 'var(--raven-warning-border)' : 'var(--raven-divider)'}`,
                              borderRadius: 6,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 8,
                              opacity: isDraggingItem ? 0.5 : 1,
                              borderTop: isDragOver && draggedIndex !== null && draggedIndex > index
                                ? '2px solid var(--raven-primary-main)'
                                : undefined,
                              borderBottom: isDragOver && draggedIndex !== null && draggedIndex < index
                                ? '2px solid var(--raven-primary-main)'
                                : undefined,
                              cursor: canReorderInstrument ? 'grab' : 'default',
                            }}
                          >
                            {/* Status indicator */}
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: 99,
                                backgroundColor: reviewStatusColor(inst.status, 'instrument'),
                                flexShrink: 0,
                              }}
                              title={inst.status === 'final_and_reviewed' ? 'Reviewed' : hasBbox ? 'Marked' : 'Not marked'}
                            />

                            {/* Drag handle */}
                            {activeDirectNodeInstruments.length > 1 && onReorderInstruments && (
                              <span
                                style={{
                                  color: 'var(--raven-text-disabled)',
                                  fontSize: 12,
                                  userSelect: 'none',
                                  cursor: canReorderInstrument ? 'grab' : 'default',
                                  opacity: canReorderInstrument ? 1 : 0.35,
                                }}
                                title={canReorderInstrument ? 'Drag to reorder' : 'Instrument-attached children are not reordered with node instruments'}
                              >
                                {'\u2807'}
                              </span>
                            )}

                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{
                                  width: 16,
                                  height: 16,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: isBranch ? 'var(--raven-warning-bg)' : 'var(--raven-surface-tier2)',
                                  color: isBranch ? 'var(--raven-warning-text)' : 'var(--raven-text-secondary)',
                                  borderRadius: 3,
                                  fontSize: 9,
                                  fontWeight: 600,
                                }}>
                                  {displaySequence}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--raven-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {getInstrumentPrimaryLabel(inst)}
                                </span>
                                {isBranch && (
                                  <span
                                    style={{
                                      fontSize: 8,
                                      padding: '1px 4px',
                                      backgroundColor: 'var(--raven-warning-bg)',
                                      color: 'var(--raven-warning-text)',
                                      borderRadius: 3,
                                      fontWeight: 600,
                                    }}
                                  >
                                    BRANCH
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--raven-text-secondary)', marginTop: 1, marginLeft: 20 }}>
                                {typeName}
                                {inst.subtype && <span> · {inst.subtype}</span>}
                                {inst.branch_ref && <span> → #{inst.branch_ref}</span>}
                              </div>
                            </div>

                            {/* Actions: Edit + Unattach */}
                            <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                              {isUnattachConfirm ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      onUnattachInstrument?.(inst.id);
                                      setConfirmUnattach(null);
                                    }}
                                    disabled={readOnly}
                                    style={{
                                      padding: '3px 8px',
                                      fontSize: 10,
                                      fontWeight: 500,
                                      color: 'white',
                                      backgroundColor: 'var(--raven-error)',
                                      border: 'none',
                                      borderRadius: 3,
                                      cursor: readOnly ? 'not-allowed' : 'pointer',
                                      opacity: readOnly ? 0.55 : 1,
                                    }}
                                  >
                                    Confirm
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmUnattach(null)}
                                    style={{
                                      padding: '3px 8px',
                                      fontSize: 10,
                                      fontWeight: 500,
                                      color: 'var(--raven-text-secondary)',
                                      backgroundColor: 'white',
                                      border: '1px solid var(--raven-divider)',
                                      borderRadius: 3,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    {'\u2715'}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => onEditInstrument?.(inst.id)}
                                    disabled={readOnly}
                                    style={{
                                      padding: '3px 8px',
                                      fontSize: 10,
                                      fontWeight: 500,
                                      color: 'var(--raven-primary-main)',
                                      backgroundColor: 'var(--raven-state-selected)',
                                      border: '1px solid var(--raven-primary-light)',
                                      borderRadius: 3,
                                      cursor: readOnly ? 'not-allowed' : 'pointer',
                                      opacity: readOnly ? 0.55 : 1,
                                    }}
                                  >
                                    Edit
                                  </button>
                                  {renderReviewButton(
                                    { kind: 'nodeInstrument', id: inst.id },
                                    inst.status,
                                    true
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => setConfirmUnattach(inst.id)}
                                    disabled={readOnly}
                                    style={{
                                      padding: '3px 6px',
                                      fontSize: 10,
                                      color: 'var(--raven-text-disabled)',
                                      backgroundColor: 'transparent',
                                      border: '1px solid var(--raven-divider)',
                                      borderRadius: 3,
                                      cursor: readOnly ? 'not-allowed' : 'pointer',
                                      opacity: readOnly ? 0.55 : 1,
                                    }}
                                    title="Unattach from node"
                                  >
                                    {'\u2715'}
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Attached Annotations */}
                {draftAttachedAnnotationIds.length > 0 && (
                  <div style={{ borderTop: '1px solid var(--raven-divider)', marginTop: 8, paddingTop: 8 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--raven-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                      Attached Annotations ({draftAttachedAnnotationIds.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
                      {draftAttachedAnnotationIds.map(id => {
                        const ann = templateAnnotations.find(a => a.id === id);
                        const label = ann?.instrument_text || ann?.template_name || id.slice(0, 8) + '...';
                        return (
                          <div
                            key={id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '4px 8px',
                              backgroundColor: 'var(--raven-state-hover)',
                              border: '1px solid var(--raven-divider)',
                              borderRadius: 4,
                              fontSize: 12,
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                              <span style={{ color: 'var(--raven-primary-main)', fontSize: 11, flexShrink: 0 }}>&#x1F4CE;</span>
                              <span style={{ color: 'var(--raven-text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {label}
                              </span>
                              {ann?.template_name && ann?.instrument_text && (
                                <span style={{ color: 'var(--raven-text-secondary)', fontSize: 10, flexShrink: 0 }}>
                                  ({ann.template_name})
                                </span>
                              )}
                            </div>
                            {onDetachAnnotation && (
                              <button
                                type="button"
                                onClick={() => onDetachAnnotation(id)}
                                disabled={readOnly}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: readOnly ? 'not-allowed' : 'pointer',
                                  color: 'var(--raven-text-disabled)',
                                  fontSize: 14,
                                  lineHeight: 1,
                                  padding: '0 2px',
                                  flexShrink: 0,
                                  opacity: readOnly ? 0.55 : 1,
                                }}
                                title="Remove attachment"
                              >
                                {'\u2715'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {onSaveAttachments && (
                      <button
                        type="button"
                        onClick={async () => {
                          setIsSavingAttachments(true);
                          try {
                            await onSaveAttachments();
                          } finally {
                            setIsSavingAttachments(false);
                          }
                        }}
                        disabled={readOnly || isSavingAttachments}
                        style={{
                          width: '100%',
                          padding: '6px 14px',
                          fontSize: 12,
                          fontWeight: 500,
                          color: isSavingAttachments ? 'var(--raven-text-disabled)' : 'var(--raven-cta-fg)',
                          backgroundColor: isSavingAttachments ? 'var(--raven-divider)' : 'var(--raven-cta-bg)',
                          border: 'none',
                          borderRadius: 5,
                          cursor: readOnly || isSavingAttachments ? 'not-allowed' : 'pointer',
                          opacity: readOnly ? 0.55 : 1,
                        }}
                      >
                        {isSavingAttachments ? 'Saving...' : 'Save Attachments'}
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4, paddingTop: 8, borderTop: '1px solid var(--raven-divider)' }}>
          {/* Left side: Clear Path option for node kind */}
          <div>
            {kind === 'node' && isMarkingExisting && onRedrawPath && (
              <button
                type="button"
                onClick={onRedrawPath}
                disabled={readOnly}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--raven-error-text)',
                  borderRadius: 6,
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  opacity: readOnly ? 0.55 : 1,
                }}
              >
                Redraw Line
              </button>
            )}
            {kind === 'node' && onClearPath && (
              <button
                type="button"
                onClick={onClearPath}
                disabled={readOnly}
                style={{
                  padding: '8px 14px',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--raven-error-text)',
                  borderRadius: 6,
                  cursor: readOnly ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 500,
                  opacity: readOnly ? 0.55 : 1,
                }}
              >
                Clear Path
              </button>
            )}
          </div>
          
          {/* Right side: Cancel/Continue and Save */}
          <div style={{ display: 'flex', gap: 8 }}>
            {kind === 'node' && onContinueDrawing ? (
              <>
                <button
                  type="button"
                  onClick={onContinueDrawing}
                  disabled={readOnly}
                  className="pnid-btn pnid-btn--sm pnid-btn--outlined"
                >
                  Continue Drawing
                </button>
                <button
                  type="submit"
                  ref={kind === 'node' ? nodeSubmitRef : undefined}
                  disabled={readOnly}
                  onKeyDown={(e) => {
                    if (!nodeQuickEnabled || nodeQuickStep !== 'submit') return;
                    if (e.key !== 'Enter' && e.key !== 'Tab') return;
                    if (e.key === 'Tab' && e.shiftKey) return;
                    e.preventDefault();
                    (e.currentTarget as HTMLButtonElement).click();
                  }}
                  className="pnid-btn pnid-btn--sm pnid-btn--contained"
                  /* The recipe owns fill/size/shadow; the node quick-entry flow
                     layers its active-step ring on top as an outline so it does
                     not fight the contained variant's border. */
                  style={
                    nodeQuickEnabled && nodeQuickStep === 'submit'
                      ? { outline: '2px solid var(--raven-primary-main)', outlineOffset: 2 }
                      : undefined
                  }
                >
                  {isMarkingExisting ? 'Save & Mark' : 'Save Line'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onCancel}
                  className="pnid-btn pnid-btn--sm pnid-btn--outlined"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  ref={kind === 'node' ? nodeSubmitRef : undefined}
                  disabled={readOnly}
                  onKeyDown={(e) => {
                    if (!nodeQuickEnabled || nodeQuickStep !== 'submit') return;
                    if (e.key !== 'Enter' && e.key !== 'Tab') return;
                    if (e.key === 'Tab' && e.shiftKey) return;
                    e.preventDefault();
                    (e.currentTarget as HTMLButtonElement).click();
                  }}
                  className="pnid-btn pnid-btn--sm pnid-btn--contained"
                  /* The recipe owns fill/size/shadow; the node quick-entry flow
                     layers its active-step ring on top as an outline so it does
                     not fight the contained variant's border. */
                  style={
                    nodeQuickEnabled && nodeQuickStep === 'submit'
                      ? { outline: '2px solid var(--raven-primary-main)', outlineOffset: 2 }
                      : undefined
                  }
                >
                  {isMarkingExisting ? 'Save & Mark' : 'Save'}
                </button>
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default PnidMarkDialog;
